import { sc } from "@/utils/uiScale";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { zoneBandSpans } from "@/utils/zoneBand";
import { RosterCanvas } from "@/canvas/RosterCanvas";
import { DENSITY_PRESETS, useGridDensity } from "@/utils/gridDensity";
import type { BlockDragEvent } from "@/canvas/RosterCanvas";
import { noteRuns, targetConflicts } from "@/utils/blockMove";
import type { OfficerBlock } from "@/utils/blockMove";
import { OfficerAssignPopover } from "@/components/OfficerAssignPopover";
import type { OfficerOption } from "@/components/OfficerAssignPopover";
import type { CellAction, NewOfficerDraft } from "@/utils/cellAction";
import { CounterSwapMoveDialog } from "@/components/CounterSwapMoveDialog";
import { useDialogScale } from "@/hooks/useDialogScale";
import { ModePill } from "@/components/ModePill";
import { SWAP_MOVE_COLORS } from "@/config/swapMoveColors";
import { apiClient, notifyViewOnlyBlocked } from "@/api/client";
import { runInOrder } from "@/utils/saveQueue";
import { useSession } from "@/contexts/SessionContext";
import { slotToHhmm } from "@/utils/partialTiming";
import { useLiveNowSlot } from "@/hooks/useLiveNowSlot";
import { LiveNowFlag } from "@/components/LiveNowLine";
import { useOfficerOrder } from "@/utils/officerOrder";
import { getOtColorSettings, useOtColorVersion } from "@/utils/otColors";
import { getModeConfig, getStartHour, generateTimeSlots, hmmmToSlot, hmmmToEndSlot, isValidTimeRange, naturalSortOfficers, formatCounterLabel, formatCounterAxisLabel } from "@/config/modeConfig";
import type { UseRosterStateReturn } from "@/hooks/useRosterState";
import { useNotification } from "@/contexts/NotificationContext";
import { ReadingRulerBands, RulerHeaderMarker, RulerToggle } from "@/components/ReadingRuler";
import { useReadingRuler, rulerColor } from "@/utils/readingRuler";
import { NoReliefCellOverlay } from "@/components/NoReliefCellOverlay";
import { useNoReliefView } from "@/contexts/NoReliefContext";
import { officerWindows, type WindowedMeta } from "@/utils/availabilityWindows";
import { CallPhoneOverlay } from "@/components/CallPhoneOverlay";
import { callIcons, nrRightInsets } from "@/utils/callIcons";
import { ackKey, CALL_ROW_MIN_HEIGHT, type CallBlock } from "@/utils/callBlocks";
import { buildZoneGroups } from "@/utils/counterZones";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DragBar } from "@/components/DragBar";
import { useDragOffset } from "@/utils/useDragOffset";
import { GridHoverCard } from "@/components/GridHoverCard";
import type { RosterGridData, HoverInfo, ManualAllocation, UnavailablePeriod, AvailabilityBracket, AvailabilityLine, OperationResult } from "@/types/api";
import type { DutyRow } from "@/components/tabs/home/gridData";
import { DUTY_FILL, DUTY_INK, NON_COUNTER_EFFECTS, NON_COUNTER_LABEL, NON_COUNTER_LEGEND_LABEL, NON_COUNTER_SHORT_LABEL } from "@/utils/counterChoice";
import { officerLabelShort } from "@/components/tabs/home/gridData";
import { COUNTER_FOCUS_EVENT, useAutoFollowCounter } from "@/utils/autoFollowCounter";

// Labels dropped their AC/DC prefix (see formatCounterAxisLabel) — the widest
// row is now the odd one out, a motor counter's shortened code ("M37A"), so
// the column can shrink from what four-character "AC40"/"DC36" labels used
// to need.
const COUNTER_LABEL_W = sc(24);
// Gutter left of the row labels that holds each zone's open/close chevron, so the
// chevron never sits on top of a counter number.
const ZONE_GUTTER_W = sc(14);
// The whole left column: gutter + labels.
const LABEL_COL_W = ZONE_GUTTER_W + COUNTER_LABEL_W;
// Height of a zone's expanded manning band (the per-hour counts under that zone).
const ZONE_STAT_H = sc(16);
// Diameter of the round chevron button that toggles a zone's band.
const ZONE_TOGGLE_D = sc(14);
const ZONE_COLOR = "#764ba2";
const HEADER_H = sc(25);
/** One lane of a duty row under the counters, and the rule above the first. */
const DUTY_LANE_H = sc(28);
const DUTY_RULE_H = 3;
const PIVOT_HEADER_H = sc(42);
const DEFAULT_HEIGHT_CALC = "calc(100dvh - 185px)";
// Icon zone at the right of a counter-view row label, past the divider line.
const LABEL_ICON_ZONE_W = sc(13);

/** Steering-wheel glyph (rim + 3-spoke hub) for a driver-side counter —
 *  a recognizable car-wheel silhouette rather than the 🛞 emoji it
 *  replaces, which loses all detail at ~7px. */
function WheelIcon({ color = "#000" }: { color?: string }) {
  return (
    <svg width="8" height="8" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeWidth="2.4" />
      <circle cx="12" cy="12" r="2.4" fill={color} />
      <path d="M3 12 H21 M12 13.5 L6.8 20 M12 13.5 L17.2 20" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Motorcycle glyph for a motor counter — the exact outline of a sportbike
 *  silhouette reference image, traced with potrace (bitmap → vector path)
 *  and simplified with svgo rather than hand-drawn, so it's a faithful
 *  reproduction of that reference (same shape, same ~1.65:1 aspect ratio)
 *  instead of an approximation. */
function MotorIcon({ color = "#000" }: { color?: string }) {
  return (
    <svg width="13" height="8" viewBox="0 0 674.5 409.5" aria-hidden="true">
      <path
        fill={color}
        d="M137 1.5c-3.4 1.8-3.2 1.5-15 15.7l-10.8 13-42.7 51.2a860 860 0 0 0-34.7 43.3 29 29 0 0 0 1.1 9c2.3 9.2 4.2 17.3 6.2 26.7 3.7 17.4 4 18.2 8.7 19.8 2.3.8 7.8 2.3 12.2 3.2l13.5 3a3480 3480 0 0 0 56.3 13c3.6.7 3.5 1.5-2.4 13-26 51-46.9 93.7-46.9 96.3 0 7.7 7 15.6 14.6 16.8 5.2.8 12.2-2.1 15.1-6.2 1.1-1.6 13.8-26.5 28.2-55.4 14.5-29 27.1-53.3 28-54.3 1.5-1.6 2.3-1.7 6.7-.6 12.8 3 15.3 4 16.3 6.8l3.5 9.6 3.5 9c1.4 3.4 6.6 17.2 14.1 37.5l11.5 31 2.5 6.5 3.8 10 7.7 20.8c2.7 7 5.1 12.9 5.5 13.2 1 .6 22.8 2.3 44 3.5l90.5 5.5 36.7 2.2 11.8.6 4-8.4a1619 1619 0 0 0 17-39.4l8.3-18.6c1.4-3.1 3-6 3.6-6.3.6-.4 5.7.7 11.3 2.4l15.8 4.6 8.5 2.5 13.5 3.9c5.8 1.7 12.1 3.5 14 4.2l6.5 1.9 8.5 2.4 10.5 3 10.8 2.9c7.6 2 10.2 2 15.3-.5 10-4.7 12-18 4.2-25.4a345 345 0 0 0-29.8-19.1L520 258.2a317.5 317.5 0 0 1-35.2-21.9c-.5-1.4.5-4.5 5.2-15l4-9c5.8-13 9.7-21 10.6-21.5.6-.4 4.6-1.2 8.8-2 4.2-.5 9.6-1.5 12.1-2l11.5-2c8.5-1.2 20.8-3.2 33.5-5.3 17.1-3 28.4-4.7 35.3-5.6 3.8-.6 7.2-1.2 7.6-1.4.4-.3 4.1-1 8.2-1.5 12.4-1.7 26.2-4.1 29.7-5.2 2.6-1 3.2-1.5 2.7-3q-1.6-4.4 6.6-5.4c5.4-.8 5.8-1.5 4-8.5-.5-2-1.7-8-2.6-13.3l-1.8-10-4.6.3c-6.7.4-7 .2-8-2.6-1-2.3-1.5-2.6-4.4-2-9.2 1.8-10.7 1.9-10.7.3q.1-1.7 1.3-3c3.2-3.4 9.5-11.4 10.6-13.5 2-3.6 1.7-38.1-.3-40-1.7-1.7-5.7-1.8-16.7-.1q-23.4 3.3-35.9 4.9c-4.7.6-9.2 1.3-10 1.6-1 .2-5.3.9-9.6 1.4l-18.4 2.5-18.4 2.6c-8.5 1-10.9 2.4-35.6 19.7-8.7 6-11.4 7-31 11.7l-28 7a526 526 0 0 1-35.8 8.5c-2.9 0-4.8-2.8-7-10.4a88 88 0 0 0-45.8-52.2c-12.3-5.5-19.3-6.6-39.4-6.6A189 189 0 0 0 254 64l-10 2.8a542 542 0 0 0-42 16c-3.9 1.7-14.5 5-16 5-1.4 0-3.6-.8-13-5l-20.5-9-10.5-4.5-9-4-8.6-3.8c-3-1.4-6-3-6.4-3.6-.6-1 3-6.1 8-11.7l9-11 12.3-14.7c6.5-7.8 5.4-16-2.5-19.3-4-1.7-4-1.7-7.8.2"
      />
      <path
        fill={color}
        d="M79.5 212.9a100 100 0 0 0-77.7 75.5c-2.2 8.8-2.5 34.3-.4 42 9.7 35.7 29.1 58.9 60.1 71.8a100 100 0 0 0 71.5 1.2l8-3.4a103 103 0 0 0 50.7-55.1c4.8-13.3 5.2-16 5.2-34.5.1-18.4 0-19.6-4.9-34-3.6-10.7-14.6-28.8-17.2-28.3-.8.2-4.9 7-9 15L158 278l2.6 7.7a63 63 0 0 1 3.3 37c-12.9 61.9-95.7 72.7-124.5 16.3a66.2 66.2 0 0 1 43.3-93.7l5.8-1.6 7.4-15.1c4.1-8.3 7.5-15.7 7.5-16.4 0-2-13.6-1.5-24 .7M552.8 213.4c-8.6 2.2-12 3.4-20.3 7.3-5.8 2.7-14 8.3-14 9.5a57 57 0 0 0 11.6 8.2c20.2 12.2 17.2 11.2 25.6 8.5a67 67 0 0 1 80.2 35 73 73 0 0 1 4 44.9 66.5 66.5 0 0 1-125.4 7.6c0-2.1-3.5-4.5-8.6-6l-10.9-3-9-2.5-5.2-1.5c-6.9-2.1 1 24.5 12.2 42a101 101 0 0 0 50 39.6c2.4 1 9.1 2.7 16.5 4 7.5 1.5 34.1.4 41-1.6a100 100 0 0 0 67.1-59 104 104 0 0 0 2.4-67 130 130 0 0 0-9.5-21c0-1.5-11-15.3-16.2-20.1A110 110 0 0 0 622 222l-7.5-3.3a101 101 0 0 0-61.7-5.3"
      />
    </svg>
  );
}

interface MousePos {
  x: number;
  y: number;
}

interface PivotStatEntry {
  time: string;
  counts: string;
  zones: string;
}

interface RosterGridProps {
  gridData: RosterGridData;
  operationMode?: string;
  shiftType?: string;
  /**
   * 'counter' (default): rows=counters, drag-to-assign officer.
   * 'officer': rows=officers, drag-to-assign counter (via OfficerScheduleGrid instead).
   */
  view?: "counter" | "officer";
  /** CSS height for the grid container. Defaults to 'calc(100dvh - 185px)'. Used
   * as-is when `growToFitRows` is off (e.g. split view, which needs a fixed pane). */
  height?: string;
  /**
   * When set, the container is never shorter than the viewport budget below, but
   * grows taller to fit every row at its (scaled) 14px floor instead of clipping/scrolling
   * them internally — the page scrolls for the rest instead. Also locks out the
   * canvas pane's own vertical scroll (horizontal/time scroll is unaffected),
   * since a bounded inner scroll region and a scrollable page fighting over the
   * same vertical swipe is confusing. Off (default) keeps the old fixed-height,
   * internally-scrollable pane split view relies on.
   */
  growToFitRows?: boolean;
  /** Ask the grid to bring a counter row (and optionally a time slot) into view and outline it.
   * Only scrolls when the row isn't already comfortably visible, never zooms or
   * changes anything else; `nonce` makes repeat requests for the same row fire again. */
  revealRequest?: { row: number; startSlot: number | null; nonce: number } | null;
  /** Full list of unavailability periods from roster state — used to show/remove them in the counter popup. */
  unavailablePeriods?: UnavailablePeriod[];
  /** Called after a mutation with the API response, so the parent can patch the grid
   * instantly from `result.snapshot` instead of waiting on a full refetch. */
  onUpdated?: (result?: Pick<OperationResult, 'snapshot'>) => void;
  /** Clear a set of [startSlot, endSlot) ranges off the shared grid state RIGHT NOW,
   * before the deleteOfficer round trip resolves — erasing a counter assignment
   * used to sit there, visibly unchanged, until the whole delete/statistics
   * round trip came back. Returns a rollback to call if the request fails. */
  onOptimisticClear?: (ranges: Array<{ officerKey: string; startSlot: number; endSlot: number }>) => () => void;
  /** Move a dragged block on the shared grid state RIGHT NOW, so a confirmed move lands
   * at once instead of after the save and a refetch. */
  onOptimisticMove?: UseRosterStateReturn['optimisticMoveBlock'];
  /** Called when user clicks an assigned officer cell (or null when a drag starts). */
  onOfficerClick?: (info: HoverInfo | null) => void;
  highlightOfficerIds?: Set<string>;
  /** Officer blocks that need a phone call (counter view): a phone icon sits on each one's top-left corner. */
  callBlocks?: CallBlock[];
  /** Keys (ackKey) of the blocks already phoned about — their icon is the quiet, done one. */
  callAcked?: Set<string>;
  /** Mark a block phoned about (true) or re-arm it (false). */
  onToggleCall?: (block: CallBlock, called: boolean) => void;
  /** Hourly (HH:00) pivot stats to show in the time header instead of bare time labels. */
  pivotStats?: PivotStatEntry[];
  /** Every stats entry (HH:00 and HH:30 alike) with its zone counts, as listed in the
   *  Manning Stats text. The per-zone bands lay these out on the grid's own time axis,
   *  merging neighbours whose count for that zone is the same. */
  zoneStats?: Array<{ time: string; zones: string }>;
  /** Present (and non-null) exactly when the Manning Stats filter is scoping what
   *  pivotStats counts — the far-away "Filter" button's own "N hidden" already says
   *  THAT it's filtered, but not while you're looking at the numbers themselves. This
   *  marks the header so it's clear the numbers aren't the full picture: an indigo
   *  strip plus a corner dot for "some officer is excluded" (which hour columns
   *  can't show), and the pivot columns outside the time window dimmed so it's also
   *  clear WHICH portion is included. */
  statsFilterInfo?: { hiddenOfficers: number; startTime: string | null; endTime: string | null };
  /** Highlighted cells from another user's recent edit — fades out after 5s. */
  editTraces?: Array<{ row: number; col: number; color: string; traceType?: 'add' | 'delete' | 'change' }>;
  /** Cells other users currently have open for editing — live, cleared on blur/disconnect (not time-based). */
  presence?: Array<{ row: number; col: number; officerKey?: string; color: string; userName: string }>;
  /** Called when a presence avatar bubble is clicked, to reveal the full user name. */
  onPresenceBubbleClick?: (userName: string, clientX: number, clientY: number) => void;
  /** Officer name/deployment search text — rendered as a compact icon in the
   * grid's corner cell (expands to an input on click) rather than a full-width
   * search bar, to save space above the grid. */
  /** Duties with a row of their own under the counters ("B@M CTRL BOOTH"), counter view only.
   *  Not counters: greyed and hatched, counted in no stats, and nothing can be dragged onto them. */
  dutyRows?: DutyRow[];
}

function slotToHmmm(slot: number, startHour: number, slotMinutes: number): string {
  const total = startHour * 60 + slot * slotMinutes;
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${h.toString().padStart(2, "0")}${m.toString().padStart(2, "0")}`;
}

function calcDurationMins(start: string, end: string, shiftType: string): number {
  const sh = getStartHour(shiftType);
  return (hmmmToEndSlot(end, sh) - hmmmToSlot(start, sh)) * 15;
}

function fmtHHMM(hhmm: string): string {
  return `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
}

const AVAIL_SOS_COLOR  = '#e879a0';
const AVAIL_OT_COLOR   = '#f59e0b';
const AVAIL_MAIN_COLOR = '#1d4ed8';

function availabilityColor(officerType: string | undefined): string {
  const type = officerType?.toUpperCase() ?? 'SOS';
  return type === 'MAIN' ? AVAIL_MAIN_COLOR : type === 'OT' ? AVAIL_OT_COLOR : AVAIL_SOS_COLOR;
}

/** An officer's availability windows as slot columns (inclusive). A multi-window officer
 *  gives one entry per segment; a full-shift window is skipped (nothing to mark). */
function officerWindowCols(
  m: WindowedMeta,
  startHour: number,
  fullShift: { start: string; end: string },
): Array<{ startCol: number; endCol: number }> {
  const segs = officerWindows(m);
  const out: Array<{ startCol: number; endCol: number }> = [];
  for (const seg of segs) {
    if (!seg.start || !seg.end) continue;
    if (seg.start === fullShift.start && seg.end === fullShift.end) continue;
    try {
      out.push({ startCol: hmmmToSlot(seg.start, startHour), endCol: hmmmToEndSlot(seg.end, startHour) - 1 });
    } catch { /* skip invalid times */ }
  }
  return out;
}

const SHIFT_BOUNDS: Record<string, { start: string; end: string }> = {
  morning: { start: '1000', end: '2200' },
  night:   { start: '2200', end: '1000' },
};

/** Legend swatch for OT: one hard-edged stripe per distinct colour in the user's OT colour
 *  settings (their timing rules plus the "other" colour). */
function otLegendBackground(): string {
  const { rules, otherColor } = getOtColorSettings();
  const colors = [...new Set([...rules.map((r) => r.color), otherColor])];
  if (colors.length === 1) return colors[0];
  const step = 100 / colors.length;
  const stops = colors.map((c, i) => `${c} ${i * step}% ${(i + 1) * step}%`);
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

export function RosterGrid({
  gridData,
  operationMode,
  shiftType = "morning",
  view = "counter",
  height,
  growToFitRows = false,
  revealRequest,
  unavailablePeriods = [],
  onUpdated,
  onOptimisticClear,
  onOptimisticMove,
  onOfficerClick,
  highlightOfficerIds,
  callBlocks,
  callAcked,
  onToggleCall,
  pivotStats,
  zoneStats,
  statsFilterInfo,
  editTraces,
  presence,
  onPresenceBubbleClick,
  dutyRows,
}: RosterGridProps) {
  useOtColorVersion(); // re-render the legend when the OT colours change
  const { canEdit } = useSession();
  const { notifyError } = useNotification();
  const liveNow = useLiveNowSlot(shiftType);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<RosterCanvas | null>(null);
  // One scroll container holds the whole grid — corner, time header, counter labels and canvas.
  // The header and the label column are position:sticky inside it, so the browser scrolls all of
  // them together natively: nothing is copied between separate boxes, so there is nothing to drift
  // (the old setup mirrored the canvas's scroll offset onto the labels by script, and on touch
  // devices and with momentum/overscroll the label column fell out of step with its rows).
  const gridPanelRef = useRef<HTMLDivElement>(null);
  const mainScrollRef = gridPanelRef;

  // The viewport-driven budget for growToFitRows — deliberately NOT measured off
  // this component's own box (see the height prop's doc comment above), and
  // deliberately NOT computed from window.innerHeight arithmetic either: on a
  // real phone, window.innerHeight can be stale relative to the browser's own
  // address-bar-aware `100dvh` (measured before the toolbar's show/hide
  // animation settles, or simply never resynced — mobile browsers don't
  // reliably fire `resize` for that transition), and cellHeight ends up
  // computed against a budget bigger than the box actually gets, clipping the
  // last few rows with nothing to reveal them (overflowY is 'hidden' here).
  // A hidden probe element with the exact same CSS height (`calc(100dvh -
  // Npx)`, real browser-maintained CSS, never touched by JS) sidesteps that
  // entirely — its measured height is what `100dvh` genuinely resolves to
  // right now, toolbar included.
  const budgetProbeRef = useRef<HTMLDivElement>(null);
  const [nominalBudgetPx, setNominalBudgetPx] = useState<number | null>(null);
  useEffect(() => {
    if (!growToFitRows || !budgetProbeRef.current) return;
    const el = budgetProbeRef.current;
    const observer = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height;
      if (h != null && h > 0) setNominalBudgetPx(Math.max(200, h));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [growToFitRows]);
  // The container's actual height once growToFitRows is on: the viewport budget,
  // or taller if even the (scaled) 14px-floor row heights don't fit in it.
  const [computedHeightPx, setComputedHeightPx] = useState<number | null>(null);
  // The outer box holds more than just the 3-panel grid (the legend row below
  // it, and conditionally the stats-filtered/pending-action banners above) —
  // sizing the box to exactly what the ROWS need, with no allowance for that
  // other chrome, squeezed the grid's own flex:1 share by however tall they
  // are and clipped the last few rows all over again. Measured once while
  // NOT yet overridden (computedHeightPx is null, so the box's height is
  // still the natural, unforced CSS value) and reused from then on — once
  // overridden, the wrapper's height is our own number, not a fresh read of
  // what the surrounding chrome actually needs.
  const outerWrapperRef = useRef<HTMLDivElement>(null);
  const chromeAroundGridRef = useRef(0);

  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  // Where the merged officer segments sit on the canvas, for the phone icons pinned to them.
  type SegmentRect = ReturnType<RosterCanvas['getSegmentRects']>[number];
  const [segmentRects, setSegmentRects] = useState<SegmentRect[]>([]);
  const segmentRectsKeyRef = useRef('');
  const { ruler, tapHandlers } = useReadingRuler();
  // The pointer, kept out of React state: it changes on every mousemove, and a state
  // update there re-rendered this whole grid dozens of times a second — the lag felt
  // most while dragging a block. The tooltip follows it by direct style writes.
  const mousePosRef = useRef<MousePos | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const placeTooltip = () => {
    const el = tooltipRef.current;
    const p = mousePosRef.current;
    if (el && p) { el.style.top = `${p.y + 16}px`; el.style.left = `${p.x + 16}px`; }
  };

  // Multi-officer picker: shown when a cell with 2+ officers is clicked
  const [multiOfficerPick, setMultiOfficerPick] = useState<{
    officers: NonNullable<HoverInfo['officers']>;
    x: number;
    y: number;
  } | null>(null);

  // Drag-to-assign/erase state: set when user finishes dragging on a counter row
  const [dragSelection, setDragSelection] = useState<{
    row: number;
    counterNum: number;
    startTime: string;
    endTime: string;
    x: number;
    y: number;
    mode: 'add' | 'erase';
    officerId?: string;
  } | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  const [overwriteConfirm, setOverwriteConfirm] = useState<{
    officerId: string;
    conflicts: Array<{ counterLabel: string; startSlot: number; endSlot: number }>;
    counterNum: number;
    startTime: string;
    endTime: string;
  } | null>(null);

  // Layout info for label column rendering — initialized with defaults so labels appear immediately
  const [layoutInfo, setLayoutInfo] = useState<{
    labelWidth: number; labelHeight: number; cellWidth: number; cellHeight: number;
    // Whole-pixel height of each row, straight off the renderer. The label column
    // must use these exact numbers rather than cellHeight (which is fractional),
    // or its boxes drift out of line with the canvas rows further down the grid.
    rowHeights: number[];
  }>({ labelWidth: 0, labelHeight: 0, cellWidth: 55, cellHeight: 15, rowHeights: [] });

  // Zones whose manning band is open under the zone's last counter. Collapsed by
  // default; a zone's divider line is then its only mark, with a tab to open it.
  const [expandedZones, setExpandedZones] = useState<Set<number>>(() => new Set());
  const hasZoneStats = !!zoneStats && zoneStats.length > 0;
  const zoneBoundaryRows = getModeConfig(operationMode).zoneBoundaryRows;
  // Any zone's tab opens or closes every zone's band together: the clicked zone's state decides which.
  const toggleZone = useCallback((zoneIdx: number) => {
    setExpandedZones((prev) => (
      prev.has(zoneIdx) ? new Set<number>() : new Set(zoneBoundaryRows.map((_, i) => i))
    ));
  }, [zoneBoundaryRows]);
  // Extra pixels the canvas leaves above each expanded zone's boundary row for its band.
  const zoneGaps = useMemo(() => {
    const gaps: Record<number, number> = {};
    if (!hasZoneStats) return gaps;
    zoneBoundaryRows.forEach((b, i) => {
      if (expandedZones.has(i)) gaps[b] = ZONE_STAT_H;
    });
    return gaps;
  }, [expandedZones, hasZoneStats, zoneBoundaryRows]);
  const zoneGapsRef = useRef(zoneGaps);
  zoneGapsRef.current = zoneGaps;

  // Pending counter action: source chosen, waiting for user to pick target
  const [pendingAction, setPendingAction] = useState<{
    action: "swap" | "move";
    sourceRow: number;
    sourceCounterNum: number;
    sourceLabel: string;
  } | null>(null);
  // The pending swap/move banner appears AFTER chromeAroundGridRef was measured,
  // so it would steal height from the (non-scrolling) grid and clip the last
  // rows. Grow the outer box by the banner's height instead, so the page scrolls.
  const pendingBannerRef = useRef<HTMLDivElement>(null);
  const [pendingBannerH, setPendingBannerH] = useState(0);
  useLayoutEffect(() => {
    const el = pendingBannerRef.current;
    setPendingBannerH(el ? el.offsetHeight + 6 : 0);
  }, [pendingAction !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dialog: both counters chosen, configure time range
  const [swapMoveDialog, setSwapMoveDialog] = useState<{
    action: "swap" | "move";
    sourceCounterNum: number;
    targetCounterNum: number;
    sourceLabel: string;
    targetLabel: string;
  } | null>(null);

  // Admin-defined batch presets, opened from the toolbar rather than from a
  // counter — a batch has no single source counter to start from.

  // Canvas highlight: which rows + slot range to overlay while dialog is open
  const [selectionHighlight, setSelectionHighlight] = useState<{
    sourceRow: number;
    targetRow: number;
    startSlot: number;
    endSlot: number;
    action: "swap" | "move";
  } | null>(null);

  // External time set by tapping canvas cells while From/To picker mode is active
  const [dialogExternalStart, setDialogExternalStart] = useState<string | null>(null);
  const [dialogExternalEnd, setDialogExternalEnd] = useState<string | null>(null);
  // Which time field the user is currently picking via cell tap
  const [activeTimePicker, setActiveTimePicker] = useState<"start" | "end" | null>(null);

  // Counter-label unavailability popup state
  const [counterMenu, setCounterMenu] = useState<{
    counterNum: number; x: number; y: number;
    addStart: string; addEnd: string; saving: boolean; error: string | null;
  } | null>(null);

  // The unavailability popup can be dragged out of the way (all devices: it has no dimmed backdrop).
  const counterMenuDrag = useDragOffset();
  // Edits made to an existing period's times, keyed by its index, until saved.
  const [periodEdits, setPeriodEdits] = useState<Record<number, { start: string; end: string }>>({});
  // A period waiting for "are you sure?" before it is removed (the popup hides meanwhile, so the dialog is on top).
  const [removePeriod, setRemovePeriod] = useState<{ index: number; counterNum: number; start: string; end: string } | null>(null);
  const counterMenuOpen = counterMenu !== null;
  useEffect(() => { if (!counterMenuOpen) { counterMenuDrag.reset(); setPeriodEdits({}); } }, [counterMenuOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const modeConfig = getModeConfig(operationMode);

  // Build sorted officer options for the popover
  const officerOrder = useOfficerOrder();   // re-sort when the user drags rows
  const officerOptions = useMemo((): OfficerOption[] => {
    const ids = naturalSortOfficers(Object.keys(gridData.officer_metadata ?? {}));
    return ids.map((id) => {
      const meta = gridData.officer_metadata?.[id];
      const label = [meta?.deployment, meta?.name].filter(Boolean).join(" - ") || id;
      return { id, label };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData.officer_metadata, officerOrder]);

  // Counter view: draw availability brackets that mirror the officer timetable.
  //
  // Rule (per user spec): check start [ and end ] independently per cell.
  //   - If a cell contains officer X AND col == X's availability_start slot → draw [
  //   - If a cell contains officer X AND col == X's availability_end-1 slot → draw ]
  // These two checks are independent; start and end may be on different counter rows.
  // Full-shift officers are skipped (no bracket needed — they span the whole row).
  const gridDataWithBrackets = useMemo((): RosterGridData => {
    if (view !== 'counter' || !gridData?.cells?.length) return gridData;
    const startHour = getStartHour(shiftType ?? 'morning');
    const meta = gridData.officer_metadata ?? {};

    const fullShift = SHIFT_BOUNDS[shiftType ?? 'morning'] ?? SHIFT_BOUNDS.morning;

    // Pre-compute the expected start/end slot for each availability WINDOW. A multi-window
    // officer contributes one entry per segment, so a bracket pair is drawn around each
    // window and the gap between them stays visible (drawing from availability_start to
    // availability_end alone would span the gap and make separate windows look merged).
    type OfficerWindow = { id: string; startCol: number; endCol: number; color: string };
    const windows: OfficerWindow[] = [];

    for (const [id, m] of Object.entries(meta)) {
      const color = availabilityColor(m.officer_type);
      for (const w of officerWindowCols(m, startHour, fullShift)) {
        windows.push({ id, startCol: w.startCol, endCol: w.endCol, color });
      }
    }

    // Scan cells: record which counter row each WINDOW is bracketed on at its exact start
    // col and end col. First occurrence wins (an officer occupies one counter at a time).
    const startCell = new Map<number, { row: number; col: number }>();
    const endCell   = new Map<number, { row: number; col: number }>();
    const winsByOfficer = new Map<string, number[]>();
    windows.forEach((w, i) => {
      const list = winsByOfficer.get(w.id);
      if (list) list.push(i); else winsByOfficer.set(w.id, [i]);
    });

    for (let row = 0; row < gridData.num_counters; row++) {
      for (let col = 0; col < gridData.num_slots; col++) {
        const cell = gridData.cells[row * gridData.num_slots + col];
        if (!cell?.officers?.length) continue;
        for (const id of cell.officers) {
          const idxs = winsByOfficer.get(id);
          if (!idxs) continue;
          for (const wi of idxs) {
            const win = windows[wi];
            if (col === win.startCol && !startCell.has(wi)) startCell.set(wi, { row, col });
            if (col === win.endCol   && !endCell.has(wi))   endCell.set(wi, { row, col });
          }
        }
      }
    }

    // Emit brackets — start and end are independent and may be on different rows.
    const brackets: AvailabilityBracket[] = [];

    windows.forEach((win, wi) => {
      const s = startCell.get(wi);
      const e = endCell.get(wi);
      if (!s && !e) return;

      if (s && e && s.row === e.row) {
        // Same row: one combined bracket
        brackets.push({ row: s.row, startCol: s.col, endCol: e.col, color: win.color });
      } else {
        if (s) brackets.push({ row: s.row, startCol: s.col, endCol: s.col, color: win.color, showEnd: false });
        if (e) brackets.push({ row: e.row, startCol: e.col, endCol: e.col, color: win.color, showStart: false });
      }
    });

    return { ...gridData, availability_brackets: brackets };
  }, [gridData, view, shiftType]);

  // Auto-activate From picker when dialog opens; clear everything when it closes
  useEffect(() => {
    if (swapMoveDialog) {
      setActiveTimePicker("start");
    } else {
      setDialogExternalStart(null);
      setDialogExternalEnd(null);
      setActiveTimePicker(null);
    }
  }, [swapMoveDialog]);

  // ── Drag-to-move one officer's block to another counter ─────────────────
  // The canvas reports the raw gesture (BlockDragEvent); this owns what it means.
  // Nothing is written on release: dropping on a counter only PROPOSES the move
  // ('confirm' phase) and an explicit Confirm button commits it, so an accidental
  // release never edits the roster. Dropping on the cancel zone, on the source
  // row, off the grid, or pressing Esc abandons it.
  const dialogScale = useDialogScale();
  const [blockMove, setBlockMove] = useState<{
    phase: "drag" | "confirm";
    block: OfficerBlock;
    sourceRow: number;
    targetRow: number | null;
    overCancel: boolean;
    saving: boolean;
    error: string | null;
  } | null>(null);
  const cancelZoneRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const dragPtrRef = useRef<{ x: number; y: number } | null>(null);
  const overCancelRef = useRef(false);

  // Distance from the viewport bottom that keeps a bar clear of everything docked
  // there: the main bottom nav, the sub-nav, and the Ctr Stats / Add Officers bar
  // (marked data-dock-bar; includes its expanded stats panel). Whichever reaches
  // highest wins, plus a small gap, so the bar rests just above and covers none
  // of them. Hidden elements (desktop has no bottom nav) measure 0 and are skipped.
  const bottomNavClearance = () => {
    let top = window.innerHeight;
    for (const el of document.querySelectorAll(".bottom-nav, .bottom-subnav, [data-dock-bar]")) {
      const r = el.getBoundingClientRect();
      if (r.height > 0 && r.bottom > window.innerHeight / 2 && r.top < window.innerHeight) top = Math.min(top, r.top);
    }
    return window.innerHeight - top + 10;
  };

  // The cancel bar is position:fixed, so its box only changes with the window: measure
  // it once per drag instead of on every pointer move, where reading layout right after
  // the ghost's last style write forced the browser to recalculate the page each time.
  const cancelRectRef = useRef<DOMRect | null>(null);
  const pointInCancelZone = (x: number, y: number) => {
    if (!cancelRectRef.current && cancelZoneRef.current) cancelRectRef.current = cancelZoneRef.current.getBoundingClientRect();
    const r = cancelRectRef.current;
    return !!r && x >= r.left - 8 && x <= r.right + 8 && y >= r.top - 8 && y <= r.bottom + 8;
  };
  const placeGhost = () => {
    const el = ghostRef.current;
    const p = dragPtrRef.current;
    if (el && p) el.style.transform = `translate(${p.x + 14}px, ${p.y - 52}px)`;
  };

  // Measured once per drag phase rather than on every render of the bars below.
  const dockClearance = useMemo(
    () => (blockMove ? bottomNavClearance() : 0),
    [blockMove?.phase], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onBlockDragRef = useRef<(e: BlockDragEvent) => void>(() => {});
  onBlockDragRef.current = (e) => {
    if (e.phase === "start") {
      onOfficerClick?.(null);
      setDragSelection(null);
      setAssignError(null);
      dragPtrRef.current = { x: e.clientX, y: e.clientY };
      overCancelRef.current = false;
      cancelRectRef.current = null;
      setBlockMove({
        phase: "drag", block: e.block, sourceRow: e.sourceRow,
        targetRow: null, overCancel: false, saving: false, error: null,
      });
      return;
    }
    dragPtrRef.current = { x: e.clientX, y: e.clientY };
    const over = pointInCancelZone(e.clientX, e.clientY);
    overCancelRef.current = over;
    const valid = !over && e.row >= 0 && e.row !== e.sourceRow && e.row < gridData.num_counters;
    const target = valid ? e.row : null;
    if (e.phase === "move") {
      // The ghost is placed by the drag effect's animation-frame loop, once per frame.
      setBlockMove((prev) =>
        prev && prev.phase === "drag" && (prev.overCancel !== over || prev.targetRow !== target)
          ? { ...prev, overCancel: over, targetRow: target }
          : prev);
    } else if (e.phase === "end") {
      setBlockMove((prev) =>
        !prev || prev.phase !== "drag" ? prev
          : target === null ? null
          : { ...prev, phase: "confirm", targetRow: target, overCancel: false });
    } else {
      setBlockMove((prev) => (prev && prev.phase === "drag" ? null : prev));
    }
  };
  const stableBlockDrag = useMemo(() => (e: BlockDragEvent) => onBlockDragRef.current(e), []);

  // Drag UI side effects: no text selection / grab cursor while dragging, and
  // edge auto-scroll so a destination off-screen is reachable mid-drag.
  useEffect(() => {
    if (blockMove?.phase !== "drag") return;
    const prevSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    placeGhost();
    const EDGE = 64;
    let raf = 0;
    const onResize = () => { cancelRectRef.current = null; };
    window.addEventListener("resize", onResize);
    const tick = () => {
      placeGhost();
      const p = dragPtrRef.current;
      if (p && !overCancelRef.current) {
        const el = mainScrollRef.current;
        const scrollable = !!el && el.scrollHeight > el.clientHeight + 1;
        const r = el?.getBoundingClientRect();
        const top = Math.max(scrollable && r ? r.top : 0, 0);
        // The cancel bar covers the bottom of the screen; its top is the effective edge.
        if (!cancelRectRef.current && cancelZoneRef.current) cancelRectRef.current = cancelZoneRef.current.getBoundingClientRect();
        const cancelTop = cancelRectRef.current?.top ?? window.innerHeight;
        const bottom = Math.min(scrollable && r ? r.bottom : window.innerHeight, window.innerHeight, cancelTop);
        let dy = 0;
        if (p.y < top + EDGE) dy = -Math.ceil(((top + EDGE - p.y) / EDGE) * 14);
        else if (p.y > bottom - EDGE) dy = Math.ceil(((p.y - (bottom - EDGE)) / EDGE) * 14);
        if (dy !== 0) {
          let moved = false;
          if (scrollable && el) {
            const before = el.scrollTop;
            el.scrollTop += dy;
            moved = el.scrollTop !== before;
          }
          if (!moved) {
            const before = window.scrollY;
            window.scrollBy(0, dy);
            moved = window.scrollY !== before;
          }
          if (moved) rendererRef.current?.reevaluateBlockDrag();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.body.style.userSelect = prevSelect;
      document.body.style.cursor = prevCursor;
    };
  }, [blockMove?.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scrolling moves the cells under a still pointer without any mousemove: have the canvas re-read
  // what is under it, so the hover card and a block drag's target follow the scroll. Capture
  // catches the grid's own scroller and the page's alike.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; rendererRef.current?.refreshPointer(); });
    };
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => { cancelAnimationFrame(raf); window.removeEventListener("scroll", onScroll, { capture: true }); };
  }, []);

  // Esc abandons a proposed move (the canvas handles Esc during the live drag).
  useEffect(() => {
    if (blockMove?.phase !== "confirm") return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setBlockMove((prev) => (prev && !prev.saving ? null : prev));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [blockMove?.phase]);

  const confirmBlockMove = async () => {
    const bm = blockMove;
    if (!bm || bm.phase !== "confirm" || bm.targetRow === null || bm.saving) return;
    const { block, sourceRow, targetRow } = bm;
    const sh = getStartHour(shiftType);
    const start = slotToHmmm(block.startCol, sh, 15);
    const end = slotToHmmm(block.endCol + 1, sh, 15);
    const meta = gridData.officer_metadata?.[block.officerId];
    const notes = gridData.assignment_notes?.[block.officerId];
    const runs = noteRuns(block, (slot) =>
      notes?.[String(slot)] ?? gridData.cells[sourceRow * gridData.num_slots + slot]?.note);
    const allocations: ManualAllocation[] = runs.map((r) => {
      const rs = slotToHmmm(r.startCol, sh, 15);
      const re = slotToHmmm(r.endCol + 1, sh, 15);
      return {
        officer: block.officerId,
        type: meta?.officer_type ?? "SOS",
        counter: targetRow + 1,
        start: rs,
        end: re,
        duration: calcDurationMins(rs, re, shiftType),
        deployment: meta?.deployment,
        name: meta?.name,
        note: r.note,
      };
    });
    const from = (gridData.counter_labels[sourceRow] ?? formatCounterLabel(sourceRow + 1, operationMode)).split("\n")[0];
    const to = (gridData.counter_labels[targetRow] ?? formatCounterLabel(targetRow + 1, operationMode)).split("\n")[0];
    // Land the move on the grid now and close the dialog; the save follows. On a
    // counter view only — the rows there are the counters the move is between.
    const optimistic = view === "counter" ? onOptimisticMove?.({
      officerKey: block.officerId,
      from: sourceRow + 1,
      to: targetRow + 1,
      startSlot: block.startCol,
      endSlot: block.endCol + 1,
    }) : undefined;
    setBlockMove(optimistic ? null : { ...bm, saving: true, error: null });
    try {
      // Queued behind any move still saving: sent side by side, two moves can land in
      // either order. A later move's grid stays drawn meanwhile — it is replayed over
      // this save's (older) snapshot until its own save confirms it.
      const result = await runInOrder(() => {
        optimistic?.sending();
        return apiClient.updateOfficerAssignments({
          officer: block.officerId,
          to_delete: [{ start, end }],
          allocations,
          description: `Moved ${block.officerId} from ${from} to ${to} (${start}-${end})`,
        });
      });
      if (!optimistic) setBlockMove(null);
      onUpdated?.(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to move";
      if (optimistic) {
        optimistic.rollback();
        onUpdated?.();
        notifyError(`Could not move ${block.officerId} to ${to}: ${message}`);
      } else {
        setBlockMove((prev) => (prev ? { ...prev, saving: false, error: message } : prev));
      }
    }
  };

  // ── Canvas drag-select callback ──────────────────────────────────────────
  const onDragSelectRef = useRef<
    (row: number, startCol: number, endCol: number, x: number, y: number, mode: 'add' | 'erase', gesture: 'click' | 'drag') => void
  >(() => {});

  onDragSelectRef.current = (row, startCol, endCol, x, y, mode, gesture) => {
    // A proposed block move is waiting on Confirm: a tap/press on another counter
    // retargets it instead of opening an add/erase popover.
    if (blockMove?.phase === "confirm") {
      if (!blockMove.saving && row !== blockMove.sourceRow) setBlockMove({ ...blockMove, targetRow: row, error: null });
      return;
    }
    // When dialog is open: only act when a From/To picker is active and user taps source/target row.
    // All other interactions are blocked while a swap/move operation is in progress.
    if (swapMoveDialog) {
      if (activeTimePicker) {
        const srcRow = swapMoveDialog.sourceCounterNum - 1;
        const tgtRow = swapMoveDialog.targetCounterNum - 1;
        if (row === srcRow || row === tgtRow) {
          const sh = getStartHour(shiftType);
          if (activeTimePicker === "start") {
            setDialogExternalStart(slotToHmmm(startCol, sh, 15));
            setActiveTimePicker("end"); // auto-advance to To picker
          } else {
            setDialogExternalEnd(slotToHmmm(endCol + 1, sh, 15));
            setActiveTimePicker(null);
          }
        }
      }
      return;
    }
    const startCell = gridData.cells[row * gridData.num_slots + startCol];
    // Single-click on an EMPTY unavailable cell → show unavailability popup (not add/delete).
    // Drag or long-press gestures bypass this and proceed to add/delete as normal.
    // Still opens in view-only — it's read-only inside (see counterMenu render below).
    if (startCell?.unavailable && !startCell.officers?.length && gesture === 'click') {
      setCounterMenu({ counterNum: row + 1, x, y, addStart: '', addEnd: '', saving: false, error: null });
      return;
    }
    // Everything past this point assigns/erases a counter — a view-only tab can
    // look at the grid but can't open either popup that would change it.
    if (!canEdit) { notifyViewOnlyBlocked(); return; }
    const startHour = getStartHour(shiftType);
    const startTime = slotToHmmm(startCol, startHour, 15);
    const endTime = slotToHmmm(endCol + 1, startHour, 15);
    const officerId = startCell?.officers?.[0];
    onOfficerClick?.(null); // dismiss edit popup when drag starts
    setDragSelection({ row, counterNum: row + 1, startTime, endTime, x, y, mode, officerId });
  };

  const stableDragSelect = useMemo(
    () =>
      (row: number, startCol: number, endCol: number, x: number, y: number, mode: 'add' | 'erase', gesture: 'click' | 'drag') =>
        onDragSelectRef.current(row, startCol, endCol, x, y, mode, gesture),
    [],
  );

  // ── Canvas click callback ────────────────────────────────────────────────
  const handleCanvasClickRef = useRef<(info: HoverInfo) => void>(() => {});
  handleCanvasClickRef.current = (info: HoverInfo) => {
    if (blockMove?.phase === "confirm") {
      if (!blockMove.saving && info.row !== blockMove.sourceRow) setBlockMove({ ...blockMove, targetRow: info.row, error: null });
      return;
    }
    // When dialog is open: intercept taps in source/target rows for picker mode.
    // On mobile, ALL short taps (filled and empty cells) come through this callback,
    // so this is the critical path for the From/To cell-tap feature.
    if (swapMoveDialog) {
      if (activeTimePicker) {
        const srcRow = swapMoveDialog.sourceCounterNum - 1;
        const tgtRow = swapMoveDialog.targetCounterNum - 1;
        if (info.row === srcRow || info.row === tgtRow) {
          const sh = getStartHour(shiftType);
          if (activeTimePicker === "start") {
            setDialogExternalStart(slotToHmmm(info.col, sh, 15));
            setActiveTimePicker("end"); // auto-advance to To picker
          } else {
            setDialogExternalEnd(slotToHmmm(info.col + 1, sh, 15));
            setActiveTimePicker(null);
          }
        }
      }
      return;
    }
    // Clicking an empty cell on an unavailable counter row → open the availability popup
    const cell = gridData.cells[info.row * gridData.num_slots + info.col];
    const px = info.clientX ?? mousePosRef.current?.x;
    const py = info.clientY ?? mousePosRef.current?.y;
    if (cell?.unavailable && !cell.officers?.length && px != null && py != null) {
      setCounterMenu({ counterNum: info.row + 1, x: px, y: py, addStart: '', addEnd: '', saving: false, error: null });
      return;
    }
    // Multi-officer cell: show picker so user can choose which officer to edit
    if (info.officers && info.officers.length > 1 && px != null && py != null) {
      setMultiOfficerPick({ officers: info.officers, x: px, y: py });
      return;
    }
    if (info.officer) {
      onOfficerClick?.(info);
      return;
    }
    // Empty cell tapped (touch only — a mouse click on an empty cell arrives via
    // the drag-select callback instead). Open the assign popover on just that
    // slot: nothing is written until Save, so a stray tap costs one dismissal.
    if (px != null && py != null) {
      if (!canEdit) { notifyViewOnlyBlocked(); return; }
      const startHour = getStartHour(shiftType);
      onOfficerClick?.(null);
      setDragSelection({
        row: info.row,
        counterNum: info.row + 1,
        startTime: slotToHmmm(info.col, startHour, 15),
        endTime: slotToHmmm(info.col + 1, startHour, 15),
        x: px,
        y: py,
        mode: 'add',
      });
    }
  };

  const stableCanvasClick = useMemo(
    () => (info: HoverInfo) => handleCanvasClickRef.current(info),
    [],
  );

  // ── Presence-bubble click callback (same stable-ref pattern as the click callback above) ──
  const onPresenceBubbleClickRef = useRef<(userName: string, clientX: number, clientY: number) => void>(() => {});
  onPresenceBubbleClickRef.current = (userName, clientX, clientY) => onPresenceBubbleClick?.(userName, clientX, clientY);
  const stablePresenceBubbleClick = useMemo(
    () => (userName: string, clientX: number, clientY: number) => onPresenceBubbleClickRef.current(userName, clientX, clientY),
    [],
  );

  // ── Officer assign handler ───────────────────────────────────────────────
  const handleOfficerAssign = async (officerId: string, force = false, newOfficer?: NewOfficerDraft) => {
    if (!dragSelection) return;
    const { counterNum, startTime, endTime } = dragSelection;

    if (!force && !newOfficer) {
      // Check if officer already has a counter assignment in this time range
      const startHour = getStartHour(shiftType ?? 'morning');
      const selStart = hmmmToSlot(startTime, startHour);
      const selEnd = hmmmToEndSlot(endTime, startHour);
      type Conflict = { counterLabel: string; startSlot: number; endSlot: number };
      const conflicts: Conflict[] = [];

      for (let row = 0; row < gridData.num_counters; row++) {
        // Already on this very counter is no clash: the range is simply theirs again.
        if (row === counterNum - 1) continue;
        let segStart: number | null = null;
        for (let slot = selStart; slot < selEnd; slot++) {
          const cell = gridData.cells[row * gridData.num_slots + slot];
          const has = cell?.officers?.includes(officerId);
          if (has && segStart === null) segStart = slot;
          if (!has && segStart !== null) {
            conflicts.push({
              counterLabel: gridData.counter_labels[row] ?? formatCounterLabel(row + 1, operationMode),
              startSlot: segStart,
              endSlot: slot,
            });
            segStart = null;
          }
        }
        if (segStart !== null) {
          conflicts.push({
            counterLabel: gridData.counter_labels[row] ?? formatCounterLabel(row + 1, operationMode),
            startSlot: segStart,
            endSlot: selEnd,
          });
        }
      }

      if (conflicts.length > 0) {
        setOverwriteConfirm({ officerId, conflicts, counterNum, startTime, endTime });
        return;
      }
    }

    const meta = newOfficer ? undefined : gridData.officer_metadata?.[officerId];
    // A new officer goes in under a placeholder key; the roster makes them a real SOS officer
    // (next free S number) in this same edit, so adding and assigning is one version.
    const allocation: ManualAllocation = {
      officer: newOfficer ? "NEW" : officerId,
      type: newOfficer ? "SOS" : (meta?.officer_type ?? "SOS"),
      counter: counterNum,
      start: startTime,
      end: endTime,
      duration: calcDurationMins(startTime, endTime, shiftType),
      deployment: newOfficer ? (newOfficer.deployment || undefined) : meta?.deployment,
      name: newOfficer ? (newOfficer.name || undefined) : meta?.name,
    };
    // A press-and-hold on someone's block that picks another officer puts the new one on in their place.
    const replaced = dragSelection.mode === 'erase' ? dragSelection.officerId : undefined;
    try {
      // One request: the swap is one action for the user, so it is one version in the history.
      const result = await apiClient.manualAllocate({
        allocations: [allocation],
        ...(replaced && replaced !== officerId ? { replace_officer: replaced } : {}),
      });
      setAssignError(null);
      setDragSelection(null);
      setOverwriteConfirm(null);
      if (rendererRef.current && gridDataWithBrackets) rendererRef.current.render(gridDataWithBrackets);
      onUpdated?.(result);
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : "Failed to assign officer");
    }
  };

  const handleCancelAssign = () => {
    setAssignError(null);
    setDragSelection(null);
    if (rendererRef.current && gridDataWithBrackets) rendererRef.current.render(gridDataWithBrackets);
  };

  // The highlight is what the user checks the range against, so it has to follow
  // the popover's time pickers — and it is also what makes a single tap visible,
  // since no drag painted anything in that case.
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer || !dragSelection) return;
    const startHour = getStartHour(shiftType);
    const first = hmmmToSlot(dragSelection.startTime, startHour);
    const last = hmmmToEndSlot(dragSelection.endTime, startHour) - 1;
    if (last >= first) renderer.paintDragHighlight(dragSelection.row, first, last, dragSelection.mode);
  }, [dragSelection, shiftType]);

  const handleRangeChange = (startTime: string, endTime: string) => {
    setAssignError(null);
    setDragSelection((sel) => (sel ? { ...sel, startTime, endTime } : sel));
  };

  // ── Clear the selected range off this counter ────────────────────────────
  /** What the hold pop-up chose for the range (see OfficerAssignPopover). */
  const handleCellAction = (action: CellAction) =>
    action.kind === 'clear' ? handleClearRange()
      : action.kind === 'officer' ? handleOfficerAssign(action.officerId, false, action.newOfficer)
        : action.kind === 'unavailable' ? handleMarkUnavailable(action.counters)
          : Promise.resolve();

  // ── Take counters out of service for the selected range ──────────────────
  /** One or several counters, one range. An officer already on one stays there (the grid flags them as usual). */
  const handleMarkUnavailable = async (counters: number[]) => {
    if (!dragSelection || counters.length === 0) return;
    const { startTime, endTime } = dragSelection;
    try {
      const result = await apiClient.addUnavailabilityBatch({ counters, start_time: startTime, end_time: endTime });
      setAssignError(null);
      setDragSelection(null);
      onUpdated?.(result);
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Failed to mark unavailable');
    }
  };

  const handleClearRange = async () => {
    if (!dragSelection?.officerId) return;
    const { officerId, startTime, endTime } = dragSelection;
    const startHour = getStartHour(shiftType);
    // Clear the grid RIGHT NOW instead of waiting on the delete/statistics round
    // trip — the popover stays open until that confirms (or rolls back), same as
    // before, so an error still has somewhere to show.
    const rollback = onOptimisticClear?.([{
      officerKey: officerId,
      startSlot: hmmmToSlot(startTime, startHour),
      endSlot: hmmmToEndSlot(endTime, startHour),
    }]);
    try {
      const result = await apiClient.deleteOfficer({
        officer: officerId,
        start_time: startTime,
        end_time: endTime,
      });
      setAssignError(null);
      setDragSelection(null);
      onUpdated?.(result);
    } catch (err) {
      rollback?.();
      setAssignError(err instanceof Error ? err.message : "Failed to delete assignment");
    }
  };

  // ── Canvas init ──────────────────────────────────────────────────────────
  // Read through a ref at init so a renderer rebuilt for a mode/view change starts at
  // the device's density; changes after that go through the effect below applyLayout.
  const [density] = useGridDensity();
  const densityRef = useRef(density);
  useEffect(() => {
    if (!canvasRef.current) return;
    const renderer = new RosterCanvas(
      canvasRef.current,
      {
        cellWidth: 55,
        cellHeight: sc(15),
        labelWidth: 0,
        labelHeight: 0,
        fontSize: sc(10),
        mergeConsecutive: true,
        zoneBoundaryRows: modeConfig.zoneBoundaryRows,
        zoneGaps: zoneGapsRef.current,
        textMode: view === "officer" ? "cell_text" : "officer_meta",
        narrowChevrons: true,
        density: densityRef.current,
        noteLane: true,
      },
      (info) => setHoverInfo(info),
      stableCanvasClick,
      stableDragSelect,
      stablePresenceBubbleClick,
    );
    rendererRef.current = renderer;
    renderer.setAfterRender(() => {
      const rects = renderer.getSegmentRects();
      const key = JSON.stringify(rects);
      if (key === segmentRectsKeyRef.current) return;
      segmentRectsKeyRef.current = key;
      setSegmentRects(rects);
    });
    if (gridDataWithBrackets) renderer.render(gridDataWithBrackets);
    return () => {
      renderer.destroy();
      rendererRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationMode, view]);

  // Block drag needs edit rights and must stay out of the way of the swap/move
  // dialog (its own cell-tap picker) and of a move already awaiting Confirm.
  const blockDragEnabled = canEdit && !swapMoveDialog && blockMove?.phase !== "confirm";
  useEffect(() => {
    rendererRef.current?.setBlockDragHandler(stableBlockDrag, blockDragEnabled);
  }, [blockDragEnabled, stableBlockDrag, operationMode, view]);

  useEffect(() => {
    rendererRef.current?.setLiveNowSlot(liveNow?.slot ?? null, liveNow?.pinned ?? false);
  }, [liveNow?.slot, operationMode, view, liveNow?.pinned]);

  // While officers are highlighted (a clicked officer, or a search) the rest of the grid is
  // dimmed; draw their availability windows as full-height lines across every counter so you
  // can see where they could still be placed.
  const availabilityLines = useMemo<AvailabilityLine[]>(() => {
    if (view !== 'counter' || !highlightOfficerIds || highlightOfficerIds.size === 0) return [];
    const startHour = getStartHour(shiftType ?? 'morning');
    const fullShift = SHIFT_BOUNDS[shiftType ?? 'morning'] ?? SHIFT_BOUNDS.morning;
    const meta = gridData.officer_metadata ?? {};
    const lines: AvailabilityLine[] = [];
    for (const id of highlightOfficerIds) {
      const m = meta[id];
      if (!m) continue;
      for (const w of officerWindowCols(m, startHour, fullShift)) {
        lines.push({ ...w, color: availabilityColor(m.officer_type) });
      }
    }
    return lines;
  }, [view, highlightOfficerIds, shiftType, gridData.officer_metadata]);

  // Keep each block's start time/label clear of its phone icon.
  // A block still owed a call reads red, for exactly as long as its phone icon is up.
  useEffect(() => {
    const pending = view === 'counter'
      ? (callBlocks ?? []).filter((b) => !callAcked?.has(ackKey(b.officerId, b.signature)))
          .map((b) => ({ officerId: b.officerId, row: b.counter, start: b.startSlot, end: b.endSlot }))
      : [];
    rendererRef.current?.setPendingCalls(pending);
  }, [view, callBlocks, callAcked, segmentRects]);

  useEffect(() => {
    const insets = new Map<string, number>();
    if (view === 'counter') {
      for (const { block, rect, size } of callIcons(callBlocks ?? [], segmentRects)) {
        insets.set(`${block.officerId}|${rect.row}|${rect.startCol}`, size + 2);
      }
    }
    rendererRef.current?.setTextInsets(insets);
  }, [view, callBlocks, segmentRects]);

  // ...and clear of its NR button.
  const { marks: noReliefMarks } = useNoReliefView();
  useEffect(() => {
    rendererRef.current?.setRightInsets(view === 'counter' ? nrRightInsets(noReliefMarks, segmentRects, 'counter') : new Map());
  }, [view, noReliefMarks, segmentRects]);

  useEffect(() => {
    if (!rendererRef.current) return;
    rendererRef.current.setAvailabilityLines(availabilityLines);
    rendererRef.current.setHighlightedOfficers(highlightOfficerIds ?? null);
  }, [highlightOfficerIds, availabilityLines]);

  // Recomputes row/cell geometry for a given (width, row-height budget) and,
  // under growToFitRows, the container's own height — the taller of that budget
  // and what every row needs at its (scaled) 14px floor, so nothing is ever clipped.
  // `growBudget` is the budget for the WHOLE box (matches nominalBudgetPx);
  // the 3-panel grid's own share of it is smaller by chromeAroundGridRef (the
  // legend row, banners) — see that ref's own doc comment for why that's
  // subtracted here rather than by each caller. Passed explicitly (not read
  // from the closure) so callers — including the ResizeObserver below, whose
  // callback is created once — always use the current value via the refs
  // kept beneath it.
  // The duty rows under the counters take their height out of the rows' budget.
  const shownDutyRows = view === 'counter' ? (dutyRows ?? []) : [];
  const dutyRowsH = shownDutyRows.length > 0
    ? DUTY_RULE_H + shownDutyRows.reduce((h, r) => h + r.lanes.length * DUTY_LANE_H + 1, 0)
    : 0;
  /** The header row's height, which the canvas area sits below inside the one scroll container. */
  const headerPx = () => (pivotStats && pivotStats.length > 0 ? PIVOT_HEADER_H : HEADER_H);
  const headerPxRef = useRef(headerPx);
  headerPxRef.current = headerPx;
  /** Lays the grid out (which redraws it); false when there was no room to, and nothing was drawn. */
  const applyLayout = (width: number, rawBudgetHeight: number, growBudget: number | null): boolean => {
    if (!rendererRef.current || width <= 0) return false;
    if (growBudget != null && computedHeightPx == null && outerWrapperRef.current && gridPanelRef.current) {
      chromeAroundGridRef.current = Math.max(
        0, outerWrapperRef.current.clientHeight - gridPanelRef.current.clientHeight,
      );
    }
    // The 3-panel grid's own gridTemplateRows reserves headerH px for the time
    // header row regardless of content — the rows below only ever get what's
    // left after that, so the row budget has to exclude it too, the same way
    // it excludes the legend/banner chrome above.
    const rowBudgetHeight = (growBudget != null
      ? rawBudgetHeight - chromeAroundGridRef.current - (pivotStats && pivotStats.length > 0 ? PIVOT_HEADER_H : HEADER_H)
      : rawBudgetHeight) - dutyRowsH;
    if (rowBudgetHeight <= 0) return false;
    rendererRef.current.resizeToFit(width, rowBudgetHeight);
    const cfg = rendererRef.current.getConfig();
    if (!cfg) return true;
    const rowHeights = cfg.rowHeights ?? [];
    // Unchanged geometry (the usual case after an edit) must not re-render the grid.
    setLayoutInfo((prev) =>
      prev.labelWidth === cfg.labelWidth && prev.labelHeight === cfg.labelHeight
        && prev.cellWidth === cfg.cellWidth && prev.cellHeight === cfg.cellHeight
        && prev.rowHeights.length === rowHeights.length && prev.rowHeights.every((h, i) => h === rowHeights[i])
        ? prev
        : { labelWidth: cfg.labelWidth, labelHeight: cfg.labelHeight, cellWidth: cfg.cellWidth, cellHeight: cfg.cellHeight, rowHeights: [...rowHeights] });
    if (growBudget != null) {
      const gapsH = Object.values(cfg.zoneGaps ?? {}).reduce((a, b) => a + b, 0);
      const rowsH = (cfg.labelHeight ?? 0) + (cfg.rowHeights ?? []).reduce((a, b) => a + b, 0) + gapsH;
      const headerH = pivotStats && pivotStats.length > 0 ? PIVOT_HEADER_H : HEADER_H;
      setComputedHeightPx(Math.max(growBudget, rowsH + headerH + dutyRowsH + chromeAroundGridRef.current));
    } else {
      setComputedHeightPx(null);
    }
    return true;
  };
  const applyLayoutRef = useRef(applyLayout);
  applyLayoutRef.current = applyLayout;

  // The scroll container's content box as the ResizeObserver last reported it — fractional
  // (e.g. 772.188), where clientWidth/clientHeight are rounded to whole pixels. Every layout
  // pass must use THIS one measurement. When the data effects below measured clientWidth
  // themselves, a roster update arriving after the observer had settled re-laid the grid out
  // on the rounded width, and since the container's size had not changed the observer never
  // corrected it: the cells ended up a fraction of a pixel off the header above them, and
  // which layout you got depended on whether a refetch landed before or after the observer.
  // Falls back to clientWidth/clientHeight only until the observer's first report, which
  // always follows and applies the real size.
  const observedBoxRef = useRef<{ width: number; height: number } | null>(null);
  const containerBox = () => {
    const el = mainScrollRef.current;
    if (!el) return null;
    return observedBoxRef.current ?? { width: el.clientWidth, height: el.clientHeight };
  };
  const growToFitRowsRef = useRef(growToFitRows);
  growToFitRowsRef.current = growToFitRows;
  const nominalBudgetPxRef = useRef(nominalBudgetPx);
  nominalBudgetPxRef.current = nominalBudgetPx;

  // Repaint when data changes — also resize to fill container after fresh data arrives.
  // Presence is set on the renderer *before* render() — it's baked into the draw
  // pipeline (below availability brackets) rather than an after-the-fact overlay.
  useEffect(() => {
    if (!rendererRef.current || !gridDataWithBrackets) return;
    rendererRef.current.setPresence(presence ?? []);
    // Laying out redraws the grid with the new data, so it is drawn once, not twice.
    rendererRef.current.setData(gridDataWithBrackets);
    const box = containerBox();
    const drawn = !!box && applyLayout(box.width - LABEL_COL_W,
      growToFitRows && nominalBudgetPx != null ? nominalBudgetPx : box.height - headerPx(),
      growToFitRows ? nominalBudgetPx : null);
    if (!drawn) rendererRef.current.render(gridDataWithBrackets);
    // Re-apply any active edit traces on top of the freshly rendered grid (traces are a
    // separate additive overlay, drawn above everything including brackets — unchanged).
    rendererRef.current.overlayEditTraces(editTraces ?? []);
  }, [gridDataWithBrackets]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opening/closing a zone band changes how much of the budget the rows get, so
  // hand the renderer its new gaps and lay the grid out again.
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer) return;
    renderer.setZoneGaps(zoneGaps);
    const box = containerBox();
    if (box) {
      applyLayout(box.width - LABEL_COL_W,
        growToFitRows && nominalBudgetPx != null ? nominalBudgetPx : box.height - headerPx(),
        growToFitRows ? nominalBudgetPx : null);
    } else if (gridDataWithBrackets) {
      renderer.render(gridDataWithBrackets);
    }
    renderer.overlayEditTraces(editTraces ?? []);
  }, [zoneGaps]); // eslint-disable-line react-hooks/exhaustive-deps

  // A duty row appearing or going changes the rows' budget: lay the grid out again.
  useEffect(() => {
    const box = containerBox();
    if (!rendererRef.current || !box) return;
    applyLayout(box.width - LABEL_COL_W,
      growToFitRows && nominalBudgetPx != null ? nominalBudgetPx : box.height - headerPx(),
      growToFitRows ? nominalBudgetPx : null);
    rendererRef.current.overlayEditTraces(editTraces ?? []);
  }, [dutyRowsH]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rows carrying a phone icon are kept tall enough for it (the icon is at most half a cell),
  // so a compact grid does not simply lose its icons.
  const callRowsKey = view === 'counter' && callBlocks
    ? [...new Set(callBlocks.map((b) => b.counter))].sort((a, b) => a - b).join(',')
    : '';
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer) return;
    renderer.setRowMinHeights(new Map(callRowsKey ? callRowsKey.split(',').map((r) => [Number(r), CALL_ROW_MIN_HEIGHT] as const) : []));
    const box = containerBox();
    if (box) {
      applyLayout(box.width - LABEL_COL_W,
        growToFitRows && nominalBudgetPx != null ? nominalBudgetPx : box.height - headerPx(),
        growToFitRows ? nominalBudgetPx : null);
    }
    renderer.overlayEditTraces(editTraces ?? []);
  }, [callRowsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new density preset changes the row floor and text sizes: lay the grid out again.
  useEffect(() => {
    densityRef.current = density;
    const renderer = rendererRef.current;
    if (!renderer) return;
    renderer.setDensity(density);
    const box = containerBox();
    if (box) {
      applyLayout(box.width - LABEL_COL_W,
        growToFitRows && nominalBudgetPx != null ? nominalBudgetPx : box.height - headerPx(),
        growToFitRows ? nominalBudgetPx : null);
    }
    renderer.overlayEditTraces(editTraces ?? []);
    // A wide layout opens on the present, not on 10:00 with "now" off-screen to the right.
    const scroller = mainScrollRef.current;
    if (scroller && DENSITY_PRESETS[density].minCellWidth > 0 && scroller.scrollLeft === 0) {
      requestAnimationFrame(() => {
        const cfg = renderer.getConfig();
        if (cfg) scroller.scrollLeft = Math.max(0, (liveNow?.slot ?? 0) * cfg.cellWidth - scroller.clientWidth * 0.3);
      });
    }
  }, [density]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply layout when the viewport budget itself changes (orientation flip,
  // browser chrome show/hide) — the ResizeObserver below only fires off this
  // component's own box, which growToFitRows deliberately stops driving off of.
  useEffect(() => {
    if (!growToFitRows || nominalBudgetPx == null || !mainScrollRef.current) return;
    applyLayout((containerBox()?.width ?? mainScrollRef.current.clientWidth) - LABEL_COL_W, nominalBudgetPx, nominalBudgetPx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nominalBudgetPx, growToFitRows]);

  // Re-render when edit traces or presence change. Presence changes far more often than
  // edit traces (every cell click vs. real edits), and since it's baked into render()
  // rather than drawn additively, any change needs a full re-render to take effect.
  useEffect(() => {
    if (!rendererRef.current) return;
    rendererRef.current.setPresence(presence ?? []);
    rendererRef.current.render(gridDataWithBrackets);
    rendererRef.current.overlayEditTraces(editTraces ?? []);
  }, [editTraces, presence]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resize canvas whenever the container changes size.
  //
  // A CSS-animated layout change nearby — the officer-edit side panel's
  // `.main-content` margin transition, or a split-divider drag driven by React
  // state on every pointermove — fires this dozens of times over the animation,
  // most reporting the exact same rounded size as the last (the container itself
  // isn't what's animating; something upstream is, and ResizeObserver still wakes
  // up to confirm nothing changed). Each call used to re-run a full canvas
  // resize+repaint and a React state set regardless, which is what turned a purely
  // cosmetic side-panel-open into visible scroll stutter. Skipping a no-op size
  // is a plain, safe win — pixel geometry genuinely hasn't changed, so there is
  // nothing to redraw.
  const lastSizeRef = useRef<{ width: number; height: number } | null>(null);
  useEffect(() => {
    if (!mainScrollRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        observedBoxRef.current = { width: entry.contentRect.width, height: entry.contentRect.height };
        // The scroll container holds the label column and header too; the canvas gets the rest.
        const width = entry.contentRect.width - LABEL_COL_W;
        const height = entry.contentRect.height - headerPxRef.current();
        if (width <= 0) continue;
        const growing = growToFitRowsRef.current;
        const budget = growing ? nominalBudgetPxRef.current : height;
        if (budget == null || budget <= 0) continue;
        const last = lastSizeRef.current;
        // Under growToFitRows the observed height is this component's OWN output
        // (see the height prop's doc comment) — comparing against it would just
        // re-confirm our last write, so only width (a real external change:
        // sidebar toggling, a split-divider drag, rotation) is worth reacting to.
        const unchanged = growing
          ? last && Math.round(last.width) === Math.round(width)
          : last && Math.round(last.width) === Math.round(width) && Math.round(last.height) === Math.round(height);
        if (unchanged) continue;
        lastSizeRef.current = { width, height };
        applyLayoutRef.current(width, budget, growing ? budget : null);
      }
    });
    observer.observe(mainScrollRef.current);
    return () => observer.disconnect();
  }, [mainScrollRef]);

  const cursor = hoverInfo?.officer ? "pointer" : "crosshair";
  const headerH = pivotStats && pivotStats.length > 0 ? PIVOT_HEADER_H : HEADER_H;

  // Row geometry read back from the renderer, so the label column and the
  // overlays land on exactly the pixels the canvas painted its rows on. Falls
  // back to the uniform cell height only before the first resize has run.
  const rowH = (row: number) => layoutInfo.rowHeights[row] ?? layoutInfo.cellHeight;
  const rowTop = (row: number) => {
    const heights = layoutInfo.rowHeights;
    // Zone bands open above a boundary row push it (and everything below) down.
    let y = 0;
    for (const key of Object.keys(zoneGaps)) if (Number(key) <= row) y += zoneGaps[Number(key)];
    if (heights.length === 0) return y + row * layoutInfo.cellHeight;
    for (let i = 0; i < row && i < heights.length; i++) y += heights[i];
    return y;
  };

  // Bring a requested counter row into view — calmly: nothing moves if the row is already
  // comfortably visible; otherwise one smooth, minimal move (centred only when the row was
  // fully off-screen, so the user keeps their bearings). Vertical scroll goes through
  // scrollIntoView so whichever ancestor actually scrolls (the page, or this pane in split
  // view) moves, and only as far as needed. Then the row's label is outlined for a moment so
  // the eye lands on it — a ring, not a dim, since dimming already means something else here.
  const autoFollow = useAutoFollowCounter();
  const lastRevealNonceRef = useRef<number | null>(null);
  const lastUserScrollRef = useRef(0);
  const flashTimerRef = useRef<number | undefined>(undefined);

  // The user's own scrolling always wins: remember when they last did it so a reveal that
  // lands moments later holds still instead of fighting their finger. Gestures inside the
  // officer card / dialogs scroll those, not the page, so they don't count.
  useEffect(() => {
    const mark = (e: Event) => {
      const t = e.target as Element | null;
      if (t?.closest?.(".officer-edit-panel, [role=dialog]")) return;
      lastUserScrollRef.current = Date.now();
    };
    const key = (e: KeyboardEvent) => {
      if (["PageUp", "PageDown", "Home", "End", "ArrowUp", "ArrowDown", " "].includes(e.key)) mark(e);
    };
    window.addEventListener("wheel", mark, { passive: true });
    window.addEventListener("touchmove", mark, { passive: true });
    window.addEventListener("keydown", key, { passive: true });
    return () => {
      window.removeEventListener("wheel", mark);
      window.removeEventListener("touchmove", mark);
      window.removeEventListener("keydown", key);
    };
  }, []);

  // Hover / focus on a row of the officer card points at its counter here. Class toggles on the
  // label itself — no React state, so hovering never re-renders the grid.
  useEffect(() => {
    let current: HTMLElement | null = null;
    const onFocus = (e: Event) => {
      const row = (e as CustomEvent<{ row: number | null }>).detail?.row;
      current?.classList.remove("counter-row-follow");
      current = row == null ? null
        : outerWrapperRef.current?.querySelector<HTMLElement>(`[data-counter-row="${row}"]`) ?? null;
      current?.classList.add("counter-row-follow");
    };
    window.addEventListener(COUNTER_FOCUS_EVENT, onFocus);
    return () => {
      window.removeEventListener(COUNTER_FOCUS_EVENT, onFocus);
      current?.classList.remove("counter-row-follow");
    };
  }, []);

  useEffect(() => {
    if (!revealRequest || revealRequest.nonce === lastRevealNonceRef.current) return;
    if (!autoFollow) { lastRevealNonceRef.current = revealRequest.nonce; return; }
    // Debounced: stepping a time dropdown with the arrow keys fires a request per step.
    const timer = window.setTimeout(() => {
      lastRevealNonceRef.current = revealRequest.nonce;
      const panel = gridPanelRef.current;
      const label = outerWrapperRef.current?.querySelector<HTMLElement>(`[data-counter-row="${revealRequest.row}"]`);
      if (!panel || !label) return;

      const reduceMotion = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      const behavior: ScrollBehavior = reduceMotion ? "auto" : "smooth";
      // visualViewport is the part of the screen actually showing: on iOS the address bar and
      // on Android the keyboard shrink it while innerHeight stays put.
      const vv = window.visualViewport;
      const viewH = vv?.height ?? window.innerHeight;
      const viewW = vv?.width ?? window.innerWidth;
      const narrow = viewW < 640;
      const MARGIN = narrow ? 16 : 36;
      // The officer card sits over the page as a bottom sheet on phones; to the browser that
      // part of the screen is still "visible", so measure what the sheet really covers and
      // treat only the strip above it as the viewport.
      const sheet = document.querySelector<HTMLElement>(".officer-edit-panel")?.getBoundingClientRect();
      const covered = sheet && sheet.top > 0 && sheet.width >= viewW - 1 ? Math.max(0, viewH - sheet.top) : 0;
      const sticky = headerH; // the time header sticks over the top of the pane

      const lr = label.getBoundingClientRect();
      const pr = panel.getBoundingClientRect();
      const visTop = Math.max(pr.top + sticky, 0) + MARGIN;
      const visBottom = Math.min(pr.bottom, viewH - covered) - MARGIN;
      const roomToShow = visBottom - visTop >= lr.height + 24; // sheet maximised: nowhere to show it
      const userIsScrolling = Date.now() - lastUserScrollRef.current < 2500;

      if (roomToShow && !userIsScrolling) {
        const fullyVisible = lr.top >= visTop && lr.bottom <= visBottom;
        const offscreen = lr.bottom < visTop - MARGIN || lr.top > visBottom + MARGIN;
        if (!fullyVisible) {
          label.style.scrollMarginTop = `${sticky + MARGIN}px`;
          label.style.scrollMarginBottom = `${MARGIN + covered}px`;
          label.scrollIntoView({ block: offscreen ? "center" : "nearest", inline: "nearest", behavior });
        }
        // Horizontal: only if the slot is out of view (past the sticky label column).
        const slot = revealRequest.startSlot;
        if (slot != null && layoutInfo.cellWidth > 0) {
          const x = LABEL_COL_W + slot * layoutInfo.cellWidth;
          const left = panel.scrollLeft + LABEL_COL_W;
          const right = panel.scrollLeft + panel.clientWidth;
          if (x < left || x + layoutInfo.cellWidth > right) {
            panel.scrollTo({ left: Math.max(0, x - LABEL_COL_W - panel.clientWidth * 0.25), behavior });
          }
        }
      }

      // Outline the label whether or not anything moved — most useful when nothing needed to.
      window.clearTimeout(flashTimerRef.current);
      label.classList.remove("counter-row-flash");
      void label.offsetWidth; // restart the animation if it was already running
      label.classList.add("counter-row-flash");
      flashTimerRef.current = window.setTimeout(() => label.classList.remove("counter-row-flash"), 1400);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [revealRequest, autoFollow]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      ref={outerWrapperRef}
      style={{
        width: "100%",
        height: growToFitRows && computedHeightPx != null
          ? `${computedHeightPx + pendingBannerH}px`
          : (height ?? DEFAULT_HEIGHT_CALC),
        minHeight: height === '100%' ? '0' : '200px',
        display: "flex",
        flexDirection: "column",
        // Long-pressing anywhere in this grid (to drag-select cells) must never
        // trigger the browser's native text-selection/callout — that's what
        // turns into a page-wide blue highlight on iOS Safari.
        userSelect: "none", WebkitUserSelect: "none",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        WebkitTouchCallout: "none" as any,
      }}
    >
      {/* Invisible probe for growToFitRows's "budget" — a real element with the
          exact height `calc(100dvh - Npx)` resolves to right now (see
          nominalBudgetPx's own doc comment for why this, not window.innerHeight
          math). Never touched by JS, so it's never part of the circular
          "what should MY height be" question the box above is answering. */}
      {growToFitRows && (
        <div
          ref={budgetProbeRef}
          aria-hidden="true"
          style={{
            position: "fixed", top: 0, left: 0, width: 0, height: height ?? DEFAULT_HEIGHT_CALC,
            visibility: "hidden", pointerEvents: "none", zIndex: -1,
          }}
        />
      )}

      {/* Stats-filtered banner — a labeled strip, not just a colored line on the
          header, so it's unambiguous that the numbers below are scoped to fewer
          officers and/or a narrower time window rather than the full roster.
          Sits above the grid (not overlaid on the header) so it never has to
          sit on top of — and potentially obscure — any of the numbers themselves. */}
      {statsFilterInfo && (
        <div
          title={statsFilterInfo.startTime || statsFilterInfo.endTime
            ? `Only ${statsFilterInfo.startTime ? fmtHHMM(statsFilterInfo.startTime) : 'the start'}–${statsFilterInfo.endTime ? fmtHHMM(statsFilterInfo.endTime) : 'the end'} of the shift is counted`
            : undefined}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "3px 10px", fontSize: 11, fontWeight: 700,
            background: "#eef2ff", color: "#4338ca", borderBottom: "1px solid #c7d2fe",
            flexShrink: 0,
          }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
            <path d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6l-6.3 8.4V19a1 1 0 0 1-1.45.9l-4-2A1 1 0 0 1 9 17v-4L2.7 4.6A1 1 0 0 1 3 4z" />
          </svg>
          {statsFilterInfo.hiddenOfficers > 0
            ? `${statsFilterInfo.hiddenOfficers} officer(s) excluded in counter stats`
            : 'Manning Stats below are filtered'}
          {(statsFilterInfo.startTime || statsFilterInfo.endTime) &&
            ` · ${statsFilterInfo.startTime ? fmtHHMM(statsFilterInfo.startTime) : 'start'}–${statsFilterInfo.endTime ? fmtHHMM(statsFilterInfo.endTime) : 'end'} only`}
        </div>
      )}

      {/* Pending counter action banner */}
      {pendingAction && (
        <div
          ref={pendingBannerRef}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 12px",
            borderRadius: 8,
            marginBottom: 6,
            background: pendingAction.action === "swap" ? "#f0fdf4" : SWAP_MOVE_COLORS.move.source.chip,
            border: `1px solid ${pendingAction.action === "swap" ? "#86efac" : SWAP_MOVE_COLORS.move.source.chipBorder}`,
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 16 }}>{pendingAction.action === "swap" ? "⇄" : "→"}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: pendingAction.action === "swap" ? "#166534" : SWAP_MOVE_COLORS.move.source.label, flex: 1 }}>
            {pendingAction.action === "swap"
              ? `Click another counter to swap with ${pendingAction.sourceLabel.split('\n')[0]}`
              : `Click destination counter to move officers from ${pendingAction.sourceLabel.split('\n')[0]}`}
          </span>
          <button
            onClick={() => setPendingAction(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 16,
              color: "#94a3b8",
              lineHeight: 1,
              padding: "0 2px",
            }}
            title="Cancel"
          >
            ×
          </button>
        </div>
      )}

      {/* ── 3-panel grid ── */}
      <div
        ref={gridPanelRef}
        style={{
          flex: 1,
          minHeight: 0,
          border: "1px solid #000",
          overflowX: "auto",
          // growToFitRows sizes this box to exactly fit every row (see the height prop's doc
          // comment) — locked so a vertical swipe here always falls through to the page.
          overflowY: growToFitRows ? "hidden" : "auto",
          position: "relative",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          WebkitOverflowScrolling: "touch" as any,
        }}
      >
       <div
        style={{
          display: "grid",
          gridTemplateRows: shownDutyRows.length > 0 ? `${headerH}px auto auto` : `${headerH}px auto`,
          gridTemplateColumns: `${LABEL_COL_W}px max-content`,
          width: "max-content",
          minWidth: "100%",
        }}
       >
        {/* Corner cell — the reading-ruler toggle (officer search lives in the pane header, beside A− / A+).
            Below it, hugging the bottom edge: the mode itself (ARR/DEP)
            — the row labels below drop their AC/DC prefix (see
            formatCounterAxisLabel) so it lives here once instead of on every
            single row. */}
        <div style={{ position: "sticky", top: 0, left: 0, zIndex: 4, background: "#f8fafc", borderRight: "1px solid #000", borderBottom: "1px solid #000", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", paddingTop: 2, paddingBottom: 1 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, marginTop: 2, maxWidth: "100%" }}>
          <RulerToggle size={Math.max(sc(17), Math.min(sc(26), headerH - sc(17)))} />
          </div>
          {view === 'counter' && <ModePill mode={operationMode} scaled />}
        </div>

        {/* Time header — x-scroll synced to main */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            background: statsFilterInfo ? "#eef2ff" : "#f8fafc",
            borderBottom: "1px solid #000",
            height: headerH,
          }}
        >
          <div
            style={{
              width: layoutInfo.cellWidth * 48,
              height: headerH,
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <RulerHeaderMarker ruler={ruler} cellWidth={layoutInfo.cellWidth} mode={operationMode} />
            {pivotStats && pivotStats.length > 0 ? (
              pivotStats.map(({ time, counts, zones }, idx) => {
                const maxLineLen = Math.max(time.length, counts.length, zones.length);
                const fs = sc(maxLineLen > 9 ? 6 : 7);
                // This hour column falls (at least partly) outside the filter's time
                // window — dimmed so it's visually clear which portion the numbers
                // above actually cover, right where the numbers themselves are.
                let excluded = false;
                if (statsFilterInfo && (statsFilterInfo.startTime || statsFilterInfo.endTime)) {
                  try {
                    const sh = getStartHour(shiftType);
                    const colStart = hmmmToSlot(time, sh);
                    const colEnd = colStart + 4;
                    const filterStart = statsFilterInfo.startTime ? hmmmToSlot(statsFilterInfo.startTime, sh) : 0;
                    const filterEnd = statsFilterInfo.endTime ? hmmmToEndSlot(statsFilterInfo.endTime, sh) : Infinity;
                    excluded = colEnd <= filterStart || colStart >= filterEnd;
                  } catch { /* unparseable time label — leave it un-dimmed */ }
                }
                return (
                <div
                  key={time}
                  style={{
                    position: "absolute",
                    left: idx * layoutInfo.cellWidth * 4,
                    width: layoutInfo.cellWidth * 4,
                    height: headerH,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: fs,
                    fontFamily: "monospace, system-ui, sans-serif",
                    borderRight: "1px solid #e2e8f0",
                    padding: "0 1px",
                    boxSizing: "border-box",
                    lineHeight: 1.4,
                    overflow: "hidden",
                    background: excluded ? "repeating-linear-gradient(135deg, #f1f5f9, #f1f5f9 3px, #e2e8f0 3px, #e2e8f0 6px)" : undefined,
                    opacity: excluded ? 0.55 : undefined,
                  }}
                  title={excluded ? "Outside the stats filter's time window — not counted" : undefined}
                >
                  <span style={{ fontWeight: 700, color: "#000", whiteSpace: "nowrap", fontSize: fs + 2 }}>{time}</span>
                  <span style={{ color: "#000", whiteSpace: "nowrap" }}>{counts}</span>
                  <span style={{ color: "#000", whiteSpace: "nowrap" }}>{zones}</span>
                </div>
                );
              })
            ) : (
              gridData.time_labels.map((label, index) => {
                if (index % 4 !== 0) return null;
                return (
                  <div
                    key={index}
                    style={{
                      position: "absolute",
                      left: index * layoutInfo.cellWidth,
                      width: layoutInfo.cellWidth * 4,
                      height: HEADER_H,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: sc(11),
                      color: "#000",
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  >
                    {label}
                  </div>
                );
              })
            )}
            {liveNow && (
              <LiveNowFlag x={liveNow.slot * layoutInfo.cellWidth} label={liveNow.label}
                totalWidth={layoutInfo.cellWidth * 48} height={headerH}
                cellWidth={layoutInfo.cellWidth} pinned={liveNow.pinned} liveLabel={liveNow.liveLabel} />
            )}
          </div>
        </div>

        {/* Counter labels column — y-scroll synced to main */}
        <div
          style={{
            position: "sticky",
            left: 0,
            zIndex: 2,
            background: "#fff",
            borderRight: "1px solid #000",
            width: LABEL_COL_W,
          }}
        >
          <div style={{ position: "relative" }}>
          {/* Zone separators — mirrors the thick purple lines the canvas draws over
              the counter cells, so the divide between the 4 zones reads as one
              continuous line spanning the label column too, not just the grid.
              Each also carries the tab that opens/closes that zone's manning band
              (the band itself sits in the gap the canvas leaves above the line). */}
          {gridData && zoneBoundaryRows.map((boundaryRow, zoneIdx) => {
            if (!(boundaryRow > 0 && boundaryRow < gridData.num_counters)) return null;
            const open = expandedZones.has(zoneIdx) && hasZoneStats;
            const lineY = rowTop(boundaryRow);
            return (
              <div key={`zone-${boundaryRow}`}>
                <div
                  style={{
                    position: "absolute", left: 0, right: 0,
                    top: lineY - 1.5, height: 3,
                    background: ZONE_COLOR, pointerEvents: "none", zIndex: 1,
                  }}
                />
                {hasZoneStats && open && (
                  <div
                    style={{
                      position: "absolute", left: 0, right: 0,
                      top: lineY - ZONE_STAT_H, height: ZONE_STAT_H,
                      background: "#f5f3ff", borderTop: "1px solid #c4b5fd",
                      boxSizing: "border-box", pointerEvents: "none",
                    }}
                  />
                )}
                {hasZoneStats && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleZone(zoneIdx); }}
                    aria-expanded={open}
                    aria-label={`${open ? "Hide" : "Show"} manning for all zones`}
                    title={`${open ? "Hide" : "Show"} manning for all zones`}
                    style={{
                      position: "absolute", left: (ZONE_GUTTER_W - ZONE_TOGGLE_D) / 2,
                      top: (open ? lineY - ZONE_STAT_H / 2 : lineY) - ZONE_TOGGLE_D / 2,
                      width: ZONE_TOGGLE_D, height: ZONE_TOGGLE_D, borderRadius: "50%",
                      padding: 0, cursor: "pointer", zIndex: 2,
                      background: open ? ZONE_COLOR : "#fff", color: open ? "#fff" : ZONE_COLOR,
                      border: `1.5px solid ${ZONE_COLOR}`, boxSizing: "border-box",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden
                      style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
                      <path d="M2.5 1 L5.5 4 L2.5 7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
          {gridData && Array.from({ length: gridData.num_counters }, (_, rowIndex) => {
            const isSource = pendingAction?.sourceRow === rowIndex;
            const isPending = pendingAction !== null;
            const pendingColor = pendingAction?.action === "swap" ? "#10b981" : SWAP_MOVE_COLORS.move.source.border;

            // While the dialog is open, highlight source/target rows in label column too.
            const isDialogSource = swapMoveDialog?.sourceCounterNum === rowIndex + 1;
            const isDialogTarget = swapMoveDialog?.targetCounterNum === rowIndex + 1;
            const dialogColors = swapMoveDialog ? SWAP_MOVE_COLORS[swapMoveDialog.action as "swap" | "move"] : null;

            return (
              <div
                key={rowIndex}
                data-counter-row={rowIndex}
                className="counter-label-overlay"
                onClick={(e) => {
                  e.stopPropagation();
                  // Block counter label clicks while dialog is open — user must dismiss it first
                  if (swapMoveDialog) return;
                  // In pending-action mode: pick this row as target (or cancel if same row)
                  if (pendingAction) {
                    if (rowIndex === pendingAction.sourceRow) {
                      setPendingAction(null);
                    } else {
                      const targetLabel = gridData.counter_labels[rowIndex] ?? `${rowIndex + 1}`;
                      setSwapMoveDialog({
                        action: pendingAction.action,
                        sourceCounterNum: pendingAction.sourceCounterNum,
                        targetCounterNum: rowIndex + 1,
                        sourceLabel: pendingAction.sourceLabel,
                        targetLabel,
                      });
                      // Pre-initialise highlight to full shift — dialog's useEffect will refine it.
                      const _startHour = getStartHour(shiftType);
                      const _opts = generateTimeSlots(shiftType);
                      setSelectionHighlight({
                        sourceRow: pendingAction.sourceCounterNum - 1,
                        targetRow: rowIndex,
                        startSlot: hmmmToSlot(_opts[0], _startHour),
                        endSlot: hmmmToEndSlot(_opts[_opts.length - 1], _startHour),
                        action: pendingAction.action,
                      });
                      setPendingAction(null);
                    }
                    return;
                  }
                  // Normal mode: open unavailability menu
                  setCounterMenu({
                    counterNum: rowIndex + 1,
                    x: e.clientX,
                    y: e.clientY,
                    addStart: '',
                    addEnd: '',
                    saving: false,
                    error: null,
                  });
                }}
                title={
                  isPending
                    ? isSource
                      ? "Click to cancel"
                      : `Click to ${pendingAction.action} with ${pendingAction.sourceLabel.split('\n')[0]}`
                    : "Tap to manage unavailable periods for this counter"
                }
                style={{
                  width: COUNTER_LABEL_W,
                  marginLeft: ZONE_GUTTER_W,
                  height: rowH(rowIndex),
                  // Room the canvas leaves above this row for an open zone band.
                  marginTop: zoneGaps[rowIndex] ?? 0,
                  cursor: isPending ? (isSource ? "not-allowed" : "crosshair") : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: sc(10),
                  color: isDialogSource
                    ? dialogColors!.source.label
                    : isDialogTarget
                    ? dialogColors!.target.label
                    : isPending ? (isSource ? pendingColor : "#000") : "#000",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                  overflow: "hidden",
                  textAlign: "center",
                  lineHeight: 1.2,
                  background: isDialogSource
                    ? dialogColors!.source.chip
                    : isDialogTarget
                    ? dialogColors!.target.chip
                    : isSource
                    ? pendingAction?.action === "swap" ? "#f0fdf4" : SWAP_MOVE_COLORS.move.source.chip
                    : isPending
                    ? "rgba(0,0,0,0.03)"
                    : ruler?.row === rowIndex
                    ? rulerColor(operationMode)
                    : "transparent",
                  borderRight: isDialogSource
                    ? `2px solid ${dialogColors!.source.border}`
                    : isDialogTarget
                    ? `2px solid ${dialogColors!.target.border}`
                    : isSource
                    ? `2px solid ${pendingColor}`
                    : undefined,
                  transition: "background 0.1s, border-right 0.1s",
                }}
              >
                {(() => {
                  // The grid's own compact axis label (bare number, plus a small icon
                  // past a divider for driver-side/motor counters) — NOT
                  // gridData.counter_labels, which still carries the full AC/DC-prefixed
                  // label everywhere else (popups, dialogs, dropdowns) where that prefix
                  // has no shared header to lean on. See formatCounterAxisLabel's own doc
                  // comment.
                  const label = view === 'counter'
                    ? formatCounterAxisLabel(rowIndex + 1, operationMode)
                    : { number: gridData.counter_labels[rowIndex] ?? `${rowIndex + 1}` };
                  // One font-size formula for every row: the number column and the icon
                  // column are fixed-width horizontal zones (not stacked lines), so the
                  // number always lands in the same place whether or not this row has an
                  // icon — that's what actually keeps "1" and "22" aligned down the column.
                  const numZoneW = COUNTER_LABEL_W - (view === 'counter' ? LABEL_ICON_ZONE_W : 0) - 2;
                  const rowFs = Math.max(6, Math.min(10, rowH(rowIndex) - 3));
                  // Shrunk further, from the label's own character count and the zone's
                  // actual width, whenever it wouldn't fit at rowFs — a motor counter's
                  // "37A" is 3 characters where most rows are 1-2, and used to overflow
                  // past the number zone instead of shrinking to fit it. ~0.62em is a bold
                  // sans-serif digit's rough width; computed, not a per-length special case,
                  // so any label length (not just "37A" specifically) sizes itself to fit.
                  const fitFs = Math.floor(numZoneW / (Math.max(1, label.number.length) * 0.62));
                  const primaryFs = Math.max(5, Math.min(rowFs, fitFs));
                  const dimmed = isPending && !isSource;
                  const iconColor = dimmed ? '#aaa' : '#000';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                      <span style={{ flex: `0 0 ${numZoneW}px`, fontWeight: 700, fontSize: primaryFs, lineHeight: 1, textAlign: 'center' }}>
                        {label.number}
                      </span>
                      {view === 'counter' && (
                        <span
                          style={{
                            flex: `0 0 ${LABEL_ICON_ZONE_W}px`,
                            height: '68%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderLeft: `1px solid ${label.icon ? '#cbd5e1' : 'transparent'}`,
                          }}
                        >
                          {label.icon === 'wheel' && <WheelIcon color={iconColor} />}
                          {label.icon === 'motor' && <MotorIcon color={iconColor} />}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
          </div>
        </div>

        {/* Main scroll area — canvas lives here */}
        <div
          style={{ position: "relative" }}
          // Observe-only: light up the pressed cell's row and column (see ReadingRuler).
          {...tapHandlers((clientX, clientY) => {
            const canvas = canvasRef.current;
            if (!canvas || !gridData) return null;
            const rect = canvas.getBoundingClientRect();
            const x = clientX - rect.left;
            const y = clientY - rect.top;
            const col = Math.floor(x / layoutInfo.cellWidth);
            let row = -1;
            for (let r = 0; r < gridData.num_counters; r++) {
              if (y >= rowTop(r) && y < rowTop(r) + rowH(r)) { row = r; break; }
            }
            return row >= 0 && col >= 0 && col < gridData.num_slots ? { row, col } : null;
          })}
        >
          <canvas
            ref={canvasRef}
            onMouseMove={(e) => { mousePosRef.current = { x: e.clientX, y: e.clientY }; placeTooltip(); }}
            onMouseLeave={() => { mousePosRef.current = null; setHoverInfo(null); }}
            // Without these, a touch long-press on the canvas triggers the browser's
            // native text-selection/callout gesture (the "whole page goes blue"
            // highlight on iOS Safari, the selection-handle magnifier on Android)
            // instead of our own long-press drag-select taking over cleanly. Scroll
            // itself stays native (untouched by these) — the long-press handlers
            // already call preventDefault() themselves once a drag actually starts.
            style={{
              cursor, display: "block",
              userSelect: "none", WebkitUserSelect: "none",
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              WebkitTouchCallout: "none" as any,
            }}
          />


          {view === 'counter' && <NoReliefCellOverlay rects={segmentRects} view="counter" operationMode={operationMode} />}
          {view === 'counter' && callBlocks && callBlocks.length > 0 && (
            <CallPhoneOverlay
              blocks={callBlocks}
              acked={callAcked}
              rects={segmentRects}
              canEdit={canEdit}
              onToggle={(block, called) => onToggleCall?.(block, called)}
            />
          )}

          {ruler && gridData && (
            <ReadingRulerBands
              ruler={ruler}
              rowTop={rowTop(ruler.row)}
              rowHeight={rowH(ruler.row)}
              cellWidth={layoutInfo.cellWidth}
              totalWidth={layoutInfo.cellWidth * gridData.num_slots}
              totalHeight={rowTop(gridData.num_counters)}
            />
          )}

          {/* ── Zone manning bands — each open zone's count per hour, sitting in the
              gap the canvas leaves just above that zone's purple line (like the
              stats row under each zone on the paper roster). Same hour columns as
              the header's pivot stats. ── */}
          {gridData && hasZoneStats && zoneBoundaryRows.map((boundaryRow, zoneIdx) => (
            expandedZones.has(zoneIdx) && boundaryRow > 0 && boundaryRow < gridData.num_counters ? (
              <div
                key={`zone-band-${boundaryRow}`}
                data-testid={`zone-stats-${zoneIdx + 1}`}
                style={{
                  position: "absolute", left: 0,
                  top: rowTop(boundaryRow) - ZONE_STAT_H, height: ZONE_STAT_H,
                  width: layoutInfo.cellWidth * 48,
                  background: "#f5f3ff", zIndex: 1,
                  borderTop: "1px solid #c4b5fd", boxSizing: "border-box",
                  fontFamily: "monospace, system-ui, sans-serif", fontSize: sc(10), fontWeight: 700, color: "#000",
                }}
              >
                {zoneBandSpans(zoneStats!, zoneIdx, getStartHour(shiftType), gridData.num_slots).map(({ startSlot, endSlot, value }) => (
                  <div
                    key={startSlot}
                    style={{
                      position: "absolute", left: startSlot * layoutInfo.cellWidth,
                      width: (endSlot - startSlot) * layoutInfo.cellWidth, height: "100%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      borderRight: `${Math.max(2, sc(2))}px solid ${ZONE_COLOR}`, boxSizing: "border-box",
                    }}
                  >
                    {value}
                  </div>
                ))}
              </div>
            ) : null
          ))}

          {/* ── Selection highlight overlay (scrolls with canvas) ─────────── */}
          {selectionHighlight && swapMoveDialog && (() => {
            const hl = selectionHighlight;
            const hlColors = SWAP_MOVE_COLORS[hl.action as "swap" | "move"];
            const cw = layoutInfo.cellWidth;
            const x = hl.startSlot * cw;
            const w = (hl.endSlot - hl.startSlot) * cw;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const baseStyle: Record<string, any> = {
              position: "absolute",
              left: x,
              width: w,
              pointerEvents: "none",
              boxSizing: "border-box",
              borderRadius: 2,
            };
            return (
              <>
                {/* Source row (A) */}
                <div
                  style={{
                    ...baseStyle,
                    top: rowTop(hl.sourceRow),
                    height: rowH(hl.sourceRow),
                    background: hlColors.source.fill,
                    border: `${activeTimePicker ? 2 : 1.5}px solid ${hlColors.source.border}`,
                  }}
                >
                  {/* "FROM" / "A" tag at left edge */}
                  <span style={{
                    position: "absolute",
                    left: 2,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: sc(8),
                    fontWeight: 700,
                    color: hlColors.source.label,
                    lineHeight: 1,
                    pointerEvents: "none",
                    background: hlColors.source.chip,
                    padding: "1px 3px",
                    borderRadius: 3,
                    opacity: w > 60 ? 1 : 0,
                  }}>
                    {hl.action === "move" ? "FROM" : "A"}
                  </span>
                </div>

                {/* Target row (B) */}
                <div
                  style={{
                    ...baseStyle,
                    top: rowTop(hl.targetRow),
                    height: rowH(hl.targetRow),
                    background: hlColors.target.fill,
                    border: `${activeTimePicker ? 2 : 1.5}px solid ${hlColors.target.border}`,
                    borderStyle: hl.action === "swap" ? "dashed" : "solid",
                  }}
                >
                  {/* "TO" / "B" tag at left edge */}
                  <span style={{
                    position: "absolute",
                    left: 2,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: sc(8),
                    fontWeight: 700,
                    color: hlColors.target.label,
                    lineHeight: 1,
                    pointerEvents: "none",
                    background: hlColors.target.chip,
                    padding: "1px 3px",
                    borderRadius: 3,
                    opacity: w > 60 ? 1 : 0,
                  }}>
                    {hl.action === "move" ? "TO" : "B"}
                  </span>
                </div>
              </>
            );
          })()}

          {/* ── Block drag-to-move: where it comes from, where it would land ── */}
          {blockMove && (() => {
            const cw = layoutInfo.cellWidth;
            const { block, sourceRow, targetRow } = blockMove;
            const left = block.startCol * cw;
            const width = (block.endCol - block.startCol + 1) * cw;
            const mc = SWAP_MOVE_COLORS.move;
            const tag = (text: string, bg: string, fg: string) => (
              <span style={{
                position: "absolute", left: 3, top: "50%", transform: "translateY(-50%)",
                fontSize: sc(9), fontWeight: 700, lineHeight: 1, padding: "2px 4px",
                borderRadius: 3, background: bg, color: fg, whiteSpace: "nowrap",
                maxWidth: "calc(100% - 6px)", overflow: "hidden", textOverflow: "ellipsis",
              }}>{text}</span>
            );
            return (
              <>
                <div
                  data-testid="block-move-source"
                  style={{
                    position: "absolute", left, width, top: rowTop(sourceRow), height: rowH(sourceRow),
                    pointerEvents: "none", boxSizing: "border-box", zIndex: 3, borderRadius: 2,
                    background: mc.source.fill, border: `2px solid ${mc.source.border}`,
                  }}
                >
                  {width > 70 && tag(`FROM ${block.officerId}`, mc.source.chip, mc.source.label)}
                </div>
                {targetRow !== null && (
                  <div
                    data-testid="block-move-target"
                    style={{
                      position: "absolute", left, width, top: rowTop(targetRow), height: rowH(targetRow),
                      pointerEvents: "none", boxSizing: "border-box", zIndex: 3, borderRadius: 2,
                      background: mc.target.fill, border: `2px solid ${mc.target.border}`,
                    }}
                  >
                    {width > 70 && tag(`TO ${block.officerId}`, mc.target.chip, mc.target.label)}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* ── Duty rows under the counters ("B@M CTRL BOOTH") — not counters: greyed and
            hatched, apart from the counters by a thick rule, and in no stats. Who is on the
            duty, and when, read off the officers' own rows (RosterState.duty_slots). ── */}
        {shownDutyRows.length > 0 && (
          <>
            <div
              style={{
                position: "sticky", left: 0, zIndex: 2, background: DUTY_FILL,
                borderRight: "1px solid #000", borderTop: `${DUTY_RULE_H}px solid #475569`, width: LABEL_COL_W,
              }}
            >
              {/* Two columns, 1:2: one merged cell down the left naming what these rows are, and each
                  deployment's own label beside its lanes on the right. */}
              <div style={{ display: "flex", width: "100%" }}>
                <div
                  title={`${NON_COUNTER_LABEL}: ${NON_COUNTER_EFFECTS.join("; ").toLowerCase()}`}
                  style={{
                    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minWidth: 0,
                    borderRight: "1px solid #64748b", boxSizing: "border-box", overflow: "hidden",
                  }}
                >
                  <span style={{
                    writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap",
                    fontSize: sc(8), fontWeight: 700, color: DUTY_INK, letterSpacing: "0.02em",
                  }}>
                    {NON_COUNTER_SHORT_LABEL}
                  </span>
                </div>
                <div style={{ flex: 2, minWidth: 0 }}>
                  {shownDutyRows.map((row) => (
                    <div
                      key={row.label}
                      title={`${row.label} — ${NON_COUNTER_LABEL.toLowerCase()}`}
                      style={{
                        height: row.lanes.length * DUTY_LANE_H, borderBottom: "1px solid #94a3b8",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        overflow: "hidden", lineHeight: 1.05, padding: "0 2px", boxSizing: "content-box",
                      }}
                    >
                      <span style={{ fontSize: sc(8), fontWeight: 700, color: DUTY_INK, textAlign: "center", whiteSpace: "nowrap" }}>
                        {row.label.startsWith("B@M") ? "B@M" : row.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div
              data-testid="duty-rows"
              style={{ borderTop: `${DUTY_RULE_H}px solid #475569`, width: layoutInfo.cellWidth * (gridData?.num_slots ?? 48) }}
            >
              {shownDutyRows.map((row) => (
                <div
                  key={row.label}
                  style={{
                    position: "relative", height: row.lanes.length * DUTY_LANE_H,
                    background: "#f8f8f8", borderBottom: "1px solid #94a3b8",
                  }}
                >
                  {row.lanes.flatMap((lane, laneIdx) => lane.map((seg) => {
                    // An officer of the other car: not on this roster, so named by what that roster printed,
                    // marked with their car, and not clickable.
                    const other = seg.otherZone;
                    const name = other
                      ? `${other.zone === "arrival" ? "ACar" : "DCar"} SN${other.sn}${other.name ? ` · ${other.name}` : ""}`
                      : officerLabelShort(seg.officerId, gridData?.officer_metadata?.[seg.officerId]);
                    const startLabel = gridData?.time_labels[seg.startSlot] ?? "";
                    const endLabel = gridData?.time_labels[seg.endSlot] ?? slotToHhmm(seg.endSlot, getStartHour(shiftType));
                    return (
                      // The same block a counter draws — border, start and end time, name — in gray.
                      <button
                        type="button"
                        key={`${seg.officerId}-${seg.startSlot}`}
                        disabled={!!other}
                        onClick={(e) => !other && onOfficerClick?.({
                          row: -1, col: seg.startSlot, officer: seg.officerId,
                          metadata: gridData?.officer_metadata?.[seg.officerId],
                          time: `${startLabel}-${endLabel}`,
                          counter: row.label, clientX: e.clientX, clientY: e.clientY,
                        })}
                        title={`${name} · ${row.label} · ${startLabel}–${endLabel}`}
                        style={{
                          position: "absolute", left: seg.startSlot * layoutInfo.cellWidth,
                          width: (seg.endSlot - seg.startSlot) * layoutInfo.cellWidth,
                          top: laneIdx * DUTY_LANE_H, height: DUTY_LANE_H,
                          background: DUTY_FILL, border: other ? "1px dashed #475569" : "1px solid #000", borderRadius: 0,
                          padding: 0, overflow: "hidden", boxSizing: "border-box", color: DUTY_INK,
                          opacity: other ? 0.75 : 1,
                          cursor: onOfficerClick && !other ? "pointer" : "default", display: "block", textAlign: "center",
                        }}
                      >
                        <span style={{ position: "absolute", left: 2, top: 1, fontSize: sc(7), fontWeight: 600, lineHeight: 1 }}>{startLabel}</span>
                        <span style={{ position: "absolute", right: 2, top: 1, fontSize: sc(7), fontWeight: 600, lineHeight: 1 }}>{endLabel}</span>
                        <span style={{
                          position: "absolute", left: 2, right: 2, bottom: 2, fontSize: sc(9), fontWeight: 700, lineHeight: 1.1,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>{name}</span>
                      </button>
                    );
                  }))}
                </div>
              ))}
            </div>
          </>
        )}
       </div>
      </div>

      {/* Color legend */}
      <div style={{ display: 'flex', gap: 10, padding: '3px 2px 2px', flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
        {([
          { color: '#a2d2ff', label: 'Main' },
          { color: '#ffc6d9', label: 'SOS' },
          { color: DUTY_FILL, label: NON_COUNTER_LEGEND_LABEL },
          { color: otLegendBackground(), label: 'OT' },
        ] as { color: string; label: string }[]).map(({ color, label }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: sc(11), color: '#64748b' }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: color, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 2, flexShrink: 0 }} />
            {label}
          </span>
        ))}
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: sc(11), color: '#64748b' }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: sc(13), lineHeight: 1, color: '#64748b' }}>#</span>
          Break
        </span>
        {view === 'counter' && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: sc(11), color: '#64748b' }}>
            <span style={{
              display: 'inline-block', width: 12, height: 12, flexShrink: 0,
              borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)',
              background: 'repeating-linear-gradient(45deg, #ccc 0px, #ccc 1.5px, #f0f0f0 1.5px, #f0f0f0 5px)',
            }} />
            Unavailable
          </span>
        )}
      </div>

      {/* Counter unavailability popup */}
      {counterMenu && !removePeriod && (() => {
        const thisCounterPeriods = unavailablePeriods
          .map((p, i) => ({ ...p, globalIndex: i }))
          .filter(p => p.counter === counterMenu.counterNum);
        // 15-min time slots covering the full shift window
        const timeOpts = generateTimeSlots(shiftType);
        const fmtOpt = (t: string) => `${t.slice(0,2)}:${t.slice(2)}`;
        const popupH = 120 + thisCounterPeriods.length * 36 + 110;
        const top = Math.min(counterMenu.y, window.innerHeight - popupH - 16);
        const left = Math.min(counterMenu.x, window.innerWidth - 264);
        const counterLabel = gridData.counter_labels[counterMenu.counterNum - 1] ?? `Counter ${counterMenu.counterNum}`;
        return (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setCounterMenu(null)} />
            <div
              style={{
                position: 'fixed',
                top: Math.max(8, top),
                left: Math.max(8, left),
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
                padding: '0 16px 16px',
                zIndex: 9999,
                width: 248,
                transform: `translate(${counterMenuDrag.offset.dx}px, ${counterMenuDrag.offset.dy}px)`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ margin: '0 -16px 12px' }}><DragBar drag={counterMenuDrag} /></div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#1e293b' }}>
                {counterLabel.split('\n')[0]}
              </div>

              {canEdit && (
                <>
                {/* Swap / Move actions (always shown, useful on mobile) */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <button
                    onClick={() => {
                      setCounterMenu(null);
                      setPendingAction({
                        action: 'swap',
                        sourceRow: counterMenu.counterNum - 1,
                        sourceCounterNum: counterMenu.counterNum,
                        sourceLabel: counterLabel,
                      });
                    }}
                    style={{
                      flex: 1, padding: '8px 4px', background: '#f0fdf4', border: '1.5px solid #86efac',
                      borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#166534',
                    }}
                  >
                    ⇄ Swap
                  </button>
                  <button
                    onClick={() => {
                      setCounterMenu(null);
                      setPendingAction({
                        action: 'move',
                        sourceRow: counterMenu.counterNum - 1,
                        sourceCounterNum: counterMenu.counterNum,
                        sourceLabel: counterLabel,
                      });
                    }}
                    style={{
                      flex: 1, padding: '8px 4px', background: SWAP_MOVE_COLORS.move.source.chip, border: `1.5px solid ${SWAP_MOVE_COLORS.move.source.chipBorder}`,
                      borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: SWAP_MOVE_COLORS.move.source.label,
                    }}
                  >
                    → Move
                  </button>
                </div>
                </>
              )}

              <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 10, letterSpacing: 0.4 }}>UNAVAILABILITY</div>

              {/* Existing periods */}
              {thisCounterPeriods.length > 0 ? (
                <div style={{ marginBottom: 12 }}>
                  {thisCounterPeriods.map(({ start_time, end_time, globalIndex }) => {
                    const edit = periodEdits[globalIndex] ?? { start: start_time, end: end_time };
                    const dirty = edit.start !== start_time || edit.end !== end_time;
                    const valid = isValidTimeRange(edit.start, edit.end, shiftType);
                    const setEdit = (patch: Partial<{ start: string; end: string }>) => {
                      setPeriodEdits((prev) => ({ ...prev, [globalIndex]: { ...edit, ...patch } }));
                      setCounterMenu((m) => m ? { ...m, error: null } : m);
                    };
                    const selectStyle = { flex: 1, minWidth: 0, padding: '4px 2px', border: `1px solid ${valid ? '#ddd' : '#dc2626'}`, borderRadius: 4, fontSize: 12 };
                    const saveEdit = async () => {
                      setCounterMenu((m) => m ? { ...m, saving: true, error: null } : m);
                      let removed = false;
                      try {
                        await apiClient.removeUnavailability({ index: globalIndex });
                        removed = true;
                        const result = await apiClient.addUnavailabilityBatch({ counters: [counterMenu.counterNum], start_time: edit.start, end_time: edit.end });
                        setCounterMenu(null);
                        onUpdated?.(result);
                      } catch (err) {
                        // The edit was refused (e.g. it overlaps another period): put the original back.
                        if (removed) {
                          await apiClient.addUnavailabilityBatch({ counters: [counterMenu.counterNum], start_time: start_time, end_time: end_time }).catch(() => undefined);
                          onUpdated?.();
                        }
                        setCounterMenu((m) => m ? { ...m, saving: false, error: err instanceof Error ? err.message : 'Failed' } : m);
                      }
                    };
                    return (
                      <div
                        key={globalIndex}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}
                      >
                        {canEdit ? (
                          <>
                            <select value={edit.start} onChange={(e) => setEdit({ start: e.target.value })} style={selectStyle} aria-label="Unavailable from">
                              {timeOpts.map((t) => <option key={t} value={t}>{fmtOpt(t)}</option>)}
                            </select>
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>–</span>
                            <select value={edit.end} onChange={(e) => setEdit({ end: e.target.value })} style={selectStyle} aria-label="Unavailable until">
                              {timeOpts.map((t) => <option key={t} value={t}>{fmtOpt(t)}</option>)}
                            </select>
                            {dirty && (
                              <button
                                disabled={!valid || counterMenu.saving}
                                onClick={saveEdit}
                                title={valid ? 'Save the new times' : 'End time must be after start time'}
                                style={{ background: valid ? '#22c55e' : '#d1d5db', color: 'white', border: 'none', borderRadius: 4, padding: '3px 7px', fontSize: 12, cursor: valid ? 'pointer' : 'not-allowed' }}
                              >
                                ✓
                              </button>
                            )}
                            <button
                              onClick={() => setRemovePeriod({ index: globalIndex, counterNum: counterMenu.counterNum, start: start_time, end: end_time })}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, padding: '0 2px', lineHeight: 1 }}
                              title="Remove this period"
                            >
                              ×
                            </button>
                          </>
                        ) : (
                          <span style={{ fontSize: 13, color: '#334155' }}>{fmtOpt(start_time)} – {fmtOpt(end_time)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>No unavailable periods set.</div>
              )}

              {/* Add unavailability form */}
              {canEdit && (
                <>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Add unavailability:</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <select
                    value={counterMenu.addStart}
                    onChange={(e) => setCounterMenu((m) => m ? { ...m, addStart: e.target.value, error: null } : m)}
                    style={{ flex: 1, padding: '4px 4px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
                  >
                    <option value="">Start</option>
                    {timeOpts.map(t => <option key={t} value={t}>{fmtOpt(t)}</option>)}
                  </select>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>–</span>
                  <select
                    value={counterMenu.addEnd}
                    onChange={(e) => setCounterMenu((m) => m ? { ...m, addEnd: e.target.value, error: null } : m)}
                    style={{ flex: 1, padding: '4px 4px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
                  >
                    <option value="">End</option>
                    {timeOpts.map(t => <option key={t} value={t}>{fmtOpt(t)}</option>)}
                  </select>
                </div>
                {counterMenu.error && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginBottom: 6 }}>{counterMenu.error}</div>
                )}
                </>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                {canEdit && (
                  <button
                    disabled={counterMenu.saving}
                    onClick={async () => {
                      if (!counterMenu.addStart || !counterMenu.addEnd) {
                        setCounterMenu((m) => m ? { ...m, error: 'Select both start and end times' } : m);
                        return;
                      }
                      if (!isValidTimeRange(counterMenu.addStart, counterMenu.addEnd, shiftType)) {
                        setCounterMenu((m) => m ? { ...m, error: 'End time must be after start time' } : m);
                        return;
                      }
                      setCounterMenu((m) => m ? { ...m, saving: true, error: null } : m);
                      try {
                        const result = await apiClient.addUnavailability({ counter: counterMenu.counterNum, start_time: counterMenu.addStart, end_time: counterMenu.addEnd });
                        setCounterMenu(null);
                        onUpdated?.(result);
                      } catch (err) {
                        setCounterMenu((m) => m ? { ...m, saving: false, error: err instanceof Error ? err.message : 'Failed' } : m);
                      }
                    }}
                    style={{ flex: 1, padding: '6px 0', background: '#667eea', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                  >
                    {counterMenu.saving ? '…' : 'Add'}
                  </button>
                )}
                <button
                  onClick={() => setCounterMenu(null)}
                  style={{ flex: 1, padding: '6px 0', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 12, cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {removePeriod && (
        <ConfirmDialog
          title="Remove unavailable period?"
          message={<>
            <strong>{(gridData.counter_labels[removePeriod.counterNum - 1] ?? `Counter ${removePeriod.counterNum}`).split('\n')[0]}</strong>{' '}
            will be available again from {removePeriod.start.slice(0, 2)}:{removePeriod.start.slice(2)} to {removePeriod.end.slice(0, 2)}:{removePeriod.end.slice(2)}.
          </>}
          confirmLabel="Remove"
          variant="danger"
          onCancel={() => setRemovePeriod(null)}
          onConfirm={async () => {
            const { index } = removePeriod;
            setRemovePeriod(null);
            try {
              const result = await apiClient.removeUnavailability({ index });
              setCounterMenu(null);
              onUpdated?.(result);
            } catch (err) {
              setCounterMenu((m) => m ? { ...m, error: err instanceof Error ? err.message : 'Failed to remove' } : m);
            }
          }}
        />
      )}

      {/* ── Hover tooltip ────────────────────────────────────────────────── */}
      {hoverInfo && mousePosRef.current && !dragSelection && (
        <GridHoverCard
          ref={tooltipRef}
          info={hoverInfo}
          x={mousePosRef.current.x}
          y={mousePosRef.current.y}
          counterLabel={hoverInfo.counter}
          canDrag
        />
      )}

      {/* ── Multi-officer picker — click on a cell shared by 2+ officers ── */}
      {multiOfficerPick && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setMultiOfficerPick(null)} />
          <div
            style={{
              position: 'fixed',
              top: Math.max(8, Math.min(multiOfficerPick.y, window.innerHeight - 180)),
              left: Math.max(8, Math.min(multiOfficerPick.x, window.innerWidth - 210)),
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              zIndex: 9999,
              minWidth: 195,
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '7px 12px', fontSize: 11, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
              Select officer to edit:
            </div>
            {multiOfficerPick.officers.map((o) => (
              <button
                key={o.id}
                onClick={() => {
                  onOfficerClick?.({
                    row: -1, col: -1,
                    officer: o.id,
                    metadata: o.metadata,
                    time: o.timeRange ?? '',
                    counter: o.counter ?? '',
                  } as HoverInfo);
                  setMultiOfficerPick(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid #f8fafc',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 13,
                  color: '#1e293b',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ fontWeight: 600 }}>{o.metadata?.name || o.id}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{o.timeRange}</div>
              </button>
            ))}
          </div>
        </>
      )}


      {/* ── OfficerAssignPopover — a tap or hold on a counter row: an empty range takes an officer; a held one
          opens on its officer, to change or clear in one step ── */}
      {dragSelection && (
        <OfficerAssignPopover
          startTime={dragSelection.startTime}
          endTime={dragSelection.endTime}
          shiftType={shiftType}
          onRangeChange={handleRangeChange}
          anchorX={dragSelection.x}
          anchorY={dragSelection.y}
          officerOptions={officerOptions}
          currentOfficerId={dragSelection.mode === 'erase' ? dragSelection.officerId : undefined}
          unavailable={{
            thisCounter: dragSelection.counterNum,
            counters: Array.from({ length: gridData.num_counters }, (_, i) => ({
              num: i + 1,
              label: (gridData.counter_labels[i] ?? formatCounterLabel(i + 1, operationMode)).split('\n')[0],
            })),
            zones: buildZoneGroups(operationMode),
          }}
          onAction={handleCellAction}
          onCancel={handleCancelAssign}
          errorMessage={assignError ?? undefined}
        />
      )}

      {/* ── Overwrite confirmation ───────────────────────────────────────── */}
      {overwriteConfirm && createPortal(
        <div
          onClick={() => setOverwriteConfirm(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, padding: 20, maxWidth: 400, width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#1e293b' }}>⚠️ Overwrite existing counter?</h3>
            {/* Officer info + full assignment card */}
            {(() => {
              const officerId = overwriteConfirm.officerId;
              const meta = gridData.officer_metadata?.[officerId];
              const startHour = getStartHour(shiftType ?? 'morning');
              const fmtSlot = (s: number) => {
                const totalMins = (startHour * 60 + s * 15) % (24 * 60);
                const h = Math.floor(totalMins / 60);
                const m = totalMins % 60;
                return `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`;
              };

              // Scan ALL rows/slots to find every counter assignment for this officer
              type Assignment = { counterLabel: string; startSlot: number; endSlot: number };
              const allAssignments: Assignment[] = [];
              for (let row = 0; row < gridData.num_counters; row++) {
                let segStart: number | null = null;
                for (let slot = 0; slot < gridData.num_slots; slot++) {
                  const cell = gridData.cells[row * gridData.num_slots + slot];
                  const has = cell?.officers?.includes(officerId);
                  if (has && segStart === null) segStart = slot;
                  if (!has && segStart !== null) {
                    allAssignments.push({ counterLabel: gridData.counter_labels[row] ?? formatCounterLabel(row + 1, operationMode), startSlot: segStart, endSlot: slot });
                    segStart = null;
                  }
                }
                if (segStart !== null) {
                  allAssignments.push({ counterLabel: gridData.counter_labels[row] ?? formatCounterLabel(row + 1, operationMode), startSlot: segStart, endSlot: gridData.num_slots });
                }
              }

              const isSOS = meta?.officer_type === 'SOS';
              const isOT  = meta?.officer_type === 'OT';
              const badgeBg   = isSOS ? '#ffc6d9' : isOT ? '#FDFD96' : '#a2d2ff';
              const badgeText = isSOS ? '#9d174d'  : isOT ? '#713f12' : '#1e40af';

              return (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                  padding: '10px 14px', marginBottom: 12 }}>
                  {/* Officer header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: badgeBg, color: badgeText, flexShrink: 0 }}>
                      {meta?.officer_type ?? '—'}
                    </span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                      {officerId}
                    </span>
                    {meta?.name && (
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta.name}</span>
                    )}
                  </div>
                  {meta?.deployment && (
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>{meta.deployment}</div>
                  )}

                  {/* All existing counter assignments */}
                  {allAssignments.length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>All current counters:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 140, overflowY: 'auto' }}>
                        {allAssignments.map((a, i) => {
                          const isConflict = overwriteConfirm.conflicts.some(c => c.counterLabel === a.counterLabel && c.startSlot === a.startSlot);
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px', borderRadius: 4, background: isConflict ? '#fef2f2' : 'white', border: isConflict ? '1px solid #fca5a5' : '1px solid #e2e8f0' }}>
                              <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: '#334155', minWidth: 36 }}>{a.counterLabel.split('\n')[0]}</span>
                              <span style={{ fontSize: 11, color: isConflict ? '#dc2626' : '#64748b' }}>{fmtSlot(a.startSlot)}–{fmtSlot(a.endSlot)}</span>
                              {isConflict && <span style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, marginLeft: 'auto' }}>conflict</span>}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
              Assigning to{' '}
              <strong>{gridData.counter_labels[overwriteConfirm.counterNum - 1] ?? formatCounterLabel(overwriteConfirm.counterNum, operationMode)}</strong>{' '}
              ({overwriteConfirm.startTime}–{overwriteConfirm.endTime}) will overwrite the existing assignment above.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setOverwriteConfirm(null)}
                style={{ flex: 1, padding: '9px 0', background: '#f1f5f9', border: '1px solid #e2e8f0',
                  borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={() => void handleOfficerAssign(overwriteConfirm.officerId, true)}
                style={{ flex: 1, padding: '9px 0', background: '#dc2626', border: 'none',
                  borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: 'white' }}
              >
                Overwrite
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}


      {/* ── Drag-to-move: floating chip + cancel zone (live drag) ─────────── */}
      {blockMove?.phase === "drag" && createPortal(
        <>
          <div
            ref={ghostRef}
            style={{
              position: "fixed", left: 0, top: 0, zIndex: 5000, pointerEvents: "none",
              padding: "5px 10px", borderRadius: 99, background: "#0f172a", color: "white",
              fontSize: 12, fontWeight: 700, boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
              whiteSpace: "nowrap",
            }}
          >
            ↕ {blockMove.block.officerId}
            {" · "}
            {fmtHHMM(slotToHmmm(blockMove.block.startCol, getStartHour(shiftType), 15))}
            –
            {fmtHHMM(slotToHmmm(blockMove.block.endCol + 1, getStartHour(shiftType), 15))}
          </div>
          <div
            ref={cancelZoneRef}
            data-testid="block-move-cancel-zone"
            style={{
              position: "fixed", bottom: dockClearance, zIndex: 4900, pointerEvents: "none",
              // Phone: full-width bar resting on the nav. Desktop: a wide centered bar.
              ...(window.innerWidth < 768
                ? { left: 0, right: 0 }
                : { left: "50%", transform: "translateX(-50%)", width: "min(420px, calc(100vw - 24px))", borderRadius: 12 }),
              height: 64, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22, fontWeight: 800, letterSpacing: 0.5, color: "white",
              // See-through so the rows under it stay visible while you aim past it.
              background: blockMove.overCancel ? "rgba(220,38,38,0.85)" : "rgba(220,38,38,0.5)",
              boxShadow: "0 -2px 12px rgba(0,0,0,0.15)", transition: "background 0.12s",
            }}
          >
            ✕ Cancel
          </div>
        </>,
        document.body,
      )}

      {/* ── Drag-to-move: confirm bar (nothing is saved until Confirm) ───── */}
      {blockMove?.phase === "confirm" && blockMove.targetRow !== null && (() => {
        const { block, sourceRow, targetRow } = blockMove;
        const label = (r: number) =>
          (gridData.counter_labels[r] ?? formatCounterLabel(r + 1, operationMode)).split("\n")[0];
        const sh = getStartHour(shiftType);
        const range = `${fmtHHMM(slotToHmmm(block.startCol, sh, 15))}–${fmtHHMM(slotToHmmm(block.endCol + 1, sh, 15))}`;
        const clash = targetConflicts(gridData.cells, gridData.num_slots, targetRow, block);
        const meta = gridData.officer_metadata?.[block.officerId];
        const mc = SWAP_MOVE_COLORS.move;
        return createPortal(
          <div
            role="dialog"
            aria-label="Confirm move"
            data-testid="block-move-confirm"
            style={{
              position: "fixed", left: "50%", bottom: dockClearance, transform: "translateX(-50%)",
              width: "min(340px, calc(100vw - 32px))", zoom: dialogScale, zIndex: 4900, background: "white",
              borderRadius: 10, padding: "8px 10px",
              boxShadow: "0 6px 32px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.08)",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 5, textAlign: "center" }}>
              {block.officerId}{meta?.name ? ` (${meta.name})` : ""} · {range}
            </div>
            {/* The two counters are the point of this bar: existing = orange, new = green. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, color: mc.source.label, textAlign: "center", marginBottom: 2 }}>FROM</div>
                <div style={{
                  textAlign: "center", padding: "7px 4px", borderRadius: 8,
                  fontSize: 18, fontWeight: 800, fontFamily: "monospace, system-ui",
                  background: mc.source.chip, border: `2px solid ${mc.source.border}`, color: mc.source.label,
                }}>{label(sourceRow)}</div>
              </div>
              <span style={{ fontSize: 18, fontWeight: 800, color: "#475569", marginTop: 12 }}>→</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, color: mc.target.label, textAlign: "center", marginBottom: 2 }}>TO</div>
                <div style={{
                  textAlign: "center", padding: "7px 4px", borderRadius: 8,
                  fontSize: 18, fontWeight: 800, fontFamily: "monospace, system-ui",
                  background: mc.target.chip, border: `2px solid ${mc.target.border}`, color: mc.target.label,
                }}>{label(targetRow)}</div>
              </div>
            </div>
            {clash.length > 0 && (
              <div style={{ fontSize: 10, color: "#991b1b", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "3px 6px", marginBottom: 6 }}>
                ⚠️ {label(targetRow)} already has {clash.join(", ")} in this range — they will share the counter.
              </div>
            )}
            {blockMove.error && (
              <div style={{ fontSize: 11, color: "#dc2626", marginBottom: 8 }}>{blockMove.error}</div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setBlockMove(null)}
                disabled={blockMove.saving}
                style={{ flex: 1, padding: "9px 0", background: "white", border: "2px solid #dc2626", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#dc2626", opacity: blockMove.saving ? 0.5 : 1 }}
              >Cancel</button>
              <button
                onClick={() => void confirmBlockMove()}
                disabled={blockMove.saving}
                style={{ flex: 1, padding: "9px 0", background: "#4f46e5", border: "2px solid #4f46e5", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", color: "white", opacity: blockMove.saving ? 0.7 : 1 }}
              >{blockMove.saving ? "Moving…" : "Confirm"}</button>
            </div>
          </div>,
          document.body,
        );
      })()}

      {/* ── Swap / Move dialog ────────────────────────────────────────────── */}
      {swapMoveDialog && (
        <CounterSwapMoveDialog
          action={swapMoveDialog.action}
          targetCounterNum={swapMoveDialog.targetCounterNum}
          sourceLabel={swapMoveDialog.sourceLabel}
          targetLabel={swapMoveDialog.targetLabel}
          shiftType={shiftType}
          gridData={gridDataWithBrackets}
          externalStartTime={dialogExternalStart ?? undefined}
          externalEndTime={dialogExternalEnd ?? undefined}
          activePicker={activeTimePicker}
          onPickerActivate={setActiveTimePicker}
          onCancel={() => { setSwapMoveDialog(null); setSelectionHighlight(null); }}
          onTimeRangeChange={(startTime, endTime) => {
            if (!swapMoveDialog || !isValidTimeRange(startTime, endTime, shiftType)) return;
            const _sh = getStartHour(shiftType);
            setSelectionHighlight({
              sourceRow: swapMoveDialog.sourceCounterNum - 1,
              targetRow: swapMoveDialog.targetCounterNum - 1,
              startSlot: hmmmToSlot(startTime, _sh),
              endSlot: hmmmToEndSlot(endTime, _sh),
              action: swapMoveDialog.action,
            });
          }}
          onConfirm={async (startTime, endTime, overwrite) => {
            // Capture state before clearing so async closure still has access
            const snap = swapMoveDialog;
            // Dismiss dialog and overlay immediately for instant visual feedback
            setSwapMoveDialog(null);
            setSelectionHighlight(null);
            const result = snap.action === "swap"
              ? await apiClient.swapCounters({
                  counter1: snap.sourceCounterNum,
                  counter2: snap.targetCounterNum,
                  start_time: startTime,
                  end_time: endTime,
                })
              : await apiClient.moveCounters({
                  counter_from: snap.sourceCounterNum,
                  counter_to: snap.targetCounterNum,
                  start_time: startTime,
                  end_time: endTime,
                  overwrite,
                });
            onUpdated?.(result);
          }}
        />
      )}
    </div>
  );
}
